import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import {
  Funnel,
  HardDriveDownload,
  Info,
  Check,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
  FolderKey,
  CalendarClock,
  HardDriveUpload,
  Settings,
} from 'lucide-react';
import { IconAdjustmentsAlt, IconShare3, IconSparkles } from '@tabler/icons-react';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../process-config/ConfigStatusView2.css';
import '../../process-config/components/ci2SharedTableStyles';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import { Ci2EditPopover, Ci2EditPopoverActions } from '../../process-config/components/Ci2EditPopover';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import './PermissoesCustomersView.css';
import PermColumnFilterPopover from './PermColumnFilterPopover';
import PermEquipPhotoModal from './PermEquipPhotoModal';
import PermEquipRowMenu from './PermEquipRowMenu';
import PermChamadoTecnicoModal from './PermChamadoTecnicoModal';
import {
  BloqueioCfgTip,
  ChamadoTecCfgTip,
  PedidoSupCfgTip,
  PERM_CFG_TIP_PORTAL_CLASS,
  isTechCallConfigured,
} from './PermCfgTooltip';

function PermCfgTipWrap({ enabled, content, children }) {
  if (!enabled) return children;
  return (
    <GoHoverTooltip
      content={content}
      portalClassName={PERM_CFG_TIP_PORTAL_CLASS}
      className="go-hover-tip-wrap--contents"
      align="end"
      delayMs={120}
      zIndex={12000}
    >
      {children}
    </GoHoverTooltip>
  );
}

const PED_SUP_OPTIONS = [
  {
    key: 'contador',
    label: 'Contador',
    help: 'Ao ativar, cliente deverá informar contadores em pedido de suprimento.',
  },
  {
    key: 'orderQty',
    label: 'Pedido Quantidade',
    help: 'Permite o cliente escolher quantos suprimentos vai pedir.',
  },
  {
    key: 'orderRma',
    label: 'Pedido RMA',
    help: 'Cliente pode informar que o pedido de suprimento é um RMA, onde precisa informar o defeito.',
  },
  {
    key: 'orderGroup',
    label: 'Agrupar Pedido',
    help: 'Ao habilitar, os pedidos serão agrupados por site, quando houver mais de um equipamento.',
  },
];

function PermSwitch({ checked, disabled, onChange, ariaLabel }) {
  return (
    <button
      type="button"
      className={`ci2-switch${checked ? ' on' : ''}`}
      disabled={disabled}
      aria-pressed={checked}
      aria-label={ariaLabel}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled && onChange) onChange(!checked);
      }}
    />
  );
}

function PermBloqueioFinanceiroPopover({ open, anchorRef, draft, busy, onChange, onCancel, onConfirm }) {
  if (!draft) return null;

  const renderBlock = (key, label, enabled, days, onToggle, onDays) => (
    <div className={`perm-bloq-fin-block${enabled ? ' is-on' : ''}`}>
      <div className="perm-bloq-fin-head">
        <span className="perm-bloq-fin-title">{label}</span>
        <PermSwitch checked={!!enabled} disabled={busy} ariaLabel={label} onChange={onToggle} />
      </div>
      {enabled ? (
        <div className="perm-bloq-fin-days-row">
          <input
            type="text"
            inputMode="numeric"
            className="perm-bloq-fin-dias-input"
            value={days}
            disabled={busy}
            aria-label={`${label} — dias`}
            placeholder="0"
            onChange={(e) => onDays(sanitizeDaysInput(e.target.value))}
          />
          <span className="perm-bloq-fin-dias-suffix">dias de inadimplência</span>
        </div>
      ) : null}
    </div>
  );

  return (
    <Ci2EditPopover
      open={open}
      anchorRef={anchorRef}
      onClose={onCancel}
      width={268}
      maxHeight={420}
      estimatedHeight={240}
      className="ci2-edit-popover--grupo perm-bloq-fin-popover"
      title="Bloqueio financeiro"
    >
      <div className="ci2-edit-popover-head ci2-edit-popover-head--shadcn">
        <h4 className="ci2-edit-popover-title">Bloqueio financeiro</h4>
        <p className="ci2-edit-popover-sub">Ative e defina dias de inadimplência</p>
      </div>
      <div className="ci2-edit-popover-body perm-bloq-fin-body">
        {renderBlock(
          'os',
          'Bloqueio OS',
          draft.os,
          draft.osDias,
          (v) => onChange({ ...draft, os: v, osDias: v ? draft.osDias : '' }),
          (v) => onChange({ ...draft, osDias: v })
        )}
        <div className="perm-bloq-fin-divider" aria-hidden />
        {renderBlock(
          'sup',
          'Bloqueio Pedido',
          draft.sup,
          draft.supDias,
          (v) => onChange({ ...draft, sup: v, supDias: v ? draft.supDias : '' }),
          (v) => onChange({ ...draft, supDias: v })
        )}
      </div>
      <Ci2EditPopoverActions onCancel={onCancel} onConfirm={onConfirm} saving={busy} />
    </Ci2EditPopover>
  );
}

function PermPedidoSuprimentoPopover({ open, anchorRef, draft, busy, onChange, onCancel, onConfirm }) {
  if (!draft) return null;
  return (
    <Ci2EditPopover
      open={open}
      anchorRef={anchorRef}
      onClose={onCancel}
      width={280}
      maxHeight={420}
      estimatedHeight={280}
      className="ci2-edit-popover--grupo perm-bloq-fin-popover perm-ped-sup-popover"
      title="Pedido de suprimento"
    >
      <div className="ci2-edit-popover-head ci2-edit-popover-head--shadcn">
        <h4 className="ci2-edit-popover-title">Pedido de suprimento</h4>
        <p className="ci2-edit-popover-sub">Opções do pedido no portal Customers</p>
      </div>
      <div className="ci2-edit-popover-body perm-bloq-fin-body">
        {PED_SUP_OPTIONS.map((opt, idx) => (
          <React.Fragment key={opt.key}>
            {idx > 0 ? <div className="perm-bloq-fin-divider" aria-hidden /> : null}
            <div className={`perm-bloq-fin-block${draft[opt.key] ? ' is-on' : ''}`}>
              <div className="perm-bloq-fin-head">
                <span className="perm-ped-sup-label-wrap">
                  <span className="perm-bloq-fin-title">{opt.label}</span>
                  <GoHoverTooltip text={opt.help} align="start" zIndex={13050}>
                    <button
                      type="button"
                      className="perm-th-info-btn perm-th-info-btn--q perm-ped-sup-info-btn"
                      aria-label={`Ajuda sobre ${opt.label}`}
                      tabIndex={-1}
                    >
                      ?
                    </button>
                  </GoHoverTooltip>
                </span>
                <PermSwitch
                  checked={!!draft[opt.key]}
                  disabled={busy}
                  ariaLabel={opt.label}
                  onChange={(v) => onChange({ ...draft, [opt.key]: v })}
                />
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
      <Ci2EditPopoverActions onCancel={onCancel} onConfirm={onConfirm} saving={busy} />
    </Ci2EditPopover>
  );
}

/** Texto com reticências: tooltip branco só quando truncado. */
function PermEllipsisText({ value, className = '', align = 'start' }) {
  const display = value != null && String(value).trim() !== '' ? String(value) : '-';
  const full = display === '-' ? '' : display;

  return (
    <GoHoverTooltip text={full} align={align} className="go-dev-ellipsis-wrap" onlyWhenTruncated>
      <span className={`go-dev-ellipsis${className ? ` ${className}` : ''}`}>{display}</span>
    </GoHoverTooltip>
  );
}

const VERVE_LOADING_MESSAGES = [
  'Estamos analisando a estrutura de grupos e subgrupos, assim que terminar, vamos otimizá-la de forma profissional. Aguarde...',
  'A Verve IA está cruzando Produto, Marca, Grupo e Subgrupo para uma segmentação mais assertiva. Aguarde...',
];

async function apiVc(path, { method = 'GET', body, signal } = {}) {
  const token = localStorage.getItem('authToken');
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
  };
  const res = await fetch(`/api/vc-usr${path}`, {
    method,
    headers,
    signal,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Erro ${res.status}`);
  }
  return json;
}

const ORDER_GROUP_CFG_FIELDS = [
  { stepId: 'sup_cfg_operacao_grouped', label: 'Operação', size: 2 },
  { stepId: 'sup_cfg_condicao_grouped', label: 'Condição', size: 3 },
  { stepId: 'sup_cfg_vendedor_grouped', label: 'Vendedor', size: 4 },
  { stepId: 'sup_cfg_transportadora_grouped', label: 'Transportadora', size: 4 },
  { stepId: 'sup_cfg_classificacao_grouped', label: 'Classificação', size: 4 },
];

const ORDER_GROUP_CFG_WHERE_HINTS = [
  'Abra Customers → Processos → Pedido de suprimentos.',
  'No fluxo, use Configuração da abertura do pedido.',
  'Na aba “(por site) Agrupado”, preencha todos os campos e salve.',
];

function normalizeOrderGroupCfgValue(raw, size) {
  return String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, size);
}

/** Campos incompletos da aba Agrupado (vazio ou tamanho diferente do exigido). */
function getMissingOrderGroupCfgFields(pedData) {
  const data = pedData && typeof pedData === 'object' ? pedData : {};
  return ORDER_GROUP_CFG_FIELDS.filter((f) => {
    const v = normalizeOrderGroupCfgValue(data[f.stepId], f.size);
    return v.length !== f.size;
  }).map((f) => f.label);
}

async function fetchPedStatusConfig() {
  const token = localStorage.getItem('authToken');
  const res = await fetch('/api/config/vc-sys-config/ped-status', {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.message || `Erro ${res.status}`);
  }
  return json.data && typeof json.data === 'object' ? json.data : {};
}

function sanitizeVerveTsvCell(v) {
  return String(v ?? '')
    .replace(/\r\n|\r|\n/g, ' ')
    .replace(/\t/g, ' ');
}

/** TSV (TAB): menos tokens que JSON por linha. Inclui todas as linhas recebidas. */
function buildVerveProdutosTsv(produtos) {
  const header = ['codigo', 'produto', 'marca', 'grupo', 'subgrupo'].join('\t');
  const lines = produtos.map((p) =>
    [
      sanitizeVerveTsvCell(p.codigo),
      sanitizeVerveTsvCell(p.produto),
      sanitizeVerveTsvCell(p.marca),
      sanitizeVerveTsvCell(p.grupo),
      sanitizeVerveTsvCell(p.subgrupo),
    ].join('\t')
  );
  return [header, ...lines].join('\n');
}

function normalizeImportCod5(raw) {
  const s0 = String(raw ?? '').trim();
  if (/^\d{5}$/.test(s0)) return s0;
  if (s0 === '') return null;
  const n = Number(s0);
  if (Number.isInteger(n) && n >= 0 && n <= 99999) {
    return String(n).padStart(5, '0');
  }
  return null;
}

function parseBitCellStrict(v, label) {
  if (v === '' || v === null || v === undefined) {
    return { ok: false, err: `${label} vazio` };
  }
  let s = String(v).trim();
  if (/^\d+\.0+$/.test(s)) s = s.replace(/\.0+$/, '');
  if (!/^[01]$/.test(s)) {
    return { ok: false, err: `${label} deve ser apenas 0 ou 1 (um dígito)` };
  }
  return { ok: true, val: s === '1' ? 1 : 0 };
}

function sheetRowLooksLikeHeader(row) {
  const a = String(row[0] ?? '').toLowerCase();
  if (/c[oó]d/.test(a)) return true;
  if (String(row[1] ?? '').toLowerCase().includes('preto')) return true;
  if (String(row[2] ?? '').toLowerCase().includes('color')) return true;
  return false;
}

function validateImportSheetRows(matrix, visibleCodSet) {
  const errors = [];
  if (!matrix.length) {
    errors.push('Planilha vazia.');
    return { ok: false, errors, items: [] };
  }
  let start = 0;
  if (matrix[0] && sheetRowLooksLikeHeader(matrix[0])) start = 1;

  const seen = new Set();
  const items = [];

  for (let i = start; i < matrix.length; i += 1) {
    const row = matrix[i];
    const excelLine = i + 1;
    if (!row || row.every((c) => c === '' || c == null)) continue;

    const cod = normalizeImportCod5(row[0]);
    if (cod == null) {
      errors.push(`Linha ${excelLine}: coluna 1 — código deve ter exatamente 5 dígitos.`);
      continue;
    }
    const pb = parseBitCellStrict(row[1], 'Preto e branco (col. 2)');
    if (!pb.ok) {
      errors.push(`Linha ${excelLine}: ${pb.err}.`);
      continue;
    }
    const cr = parseBitCellStrict(row[2], 'Colorido (col. 3)');
    if (!cr.ok) {
      errors.push(`Linha ${excelLine}: ${cr.err}.`);
      continue;
    }
    if (pb.val === cr.val) {
      errors.push(
        `Linha ${excelLine}: preto e branco e colorido devem ser opostos (um 1 e outro 0).`
      );
      continue;
    }
    if (!visibleCodSet.has(cod)) {
      errors.push(
        `Linha ${excelLine}: código ${cod} não está na listagem visível (filtros atuais).`
      );
      continue;
    }
    if (seen.has(cod)) {
      errors.push(`Linha ${excelLine}: código ${cod} repetido na planilha.`);
      continue;
    }
    seen.add(cod);
    items.push({ codprod: cod, typesup: pb.val === 1 ? 'bk' : 'color' });
  }

  if (!items.length && errors.length === 0) {
    errors.push('Nenhuma linha válida (colunas A: código, B: P&B, C: color).');
  }

  const hasErr = errors.length > 0;
  return {
    ok: !hasErr && items.length > 0,
    errors,
    items: hasErr ? [] : items,
  };
}

function readXlsxAsMatrix(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const name = wb.SheetNames[0];
        if (!name) {
          reject(new Error('Planilha sem abas.'));
          return;
        }
        const matrix = XLSX.utils.sheet_to_json(wb.Sheets[name], {
          header: 1,
          raw: true,
          defval: '',
        });
        resolve(matrix);
      } catch (e) {
        reject(e);
      }
    };
    reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
    reader.readAsArrayBuffer(file);
  });
}

function isXlsxFile(file) {
  if (!file || !file.name) return false;
  const n = file.name.toLowerCase();
  return n.endsWith('.xlsx');
}

function sanitizeDaysInput(raw) {
  return String(raw ?? '')
    .replace(/\D/g, '')
    .slice(0, 3);
}

function normClienteGrupoKey(row) {
  const g = String(row?.grupo || '').trim();
  return g || '-';
}

async function apiGeminiVerveOrganizeSuprimentos(tsv, signal) {
  const token = localStorage.getItem('authToken');
  const res = await fetch('/api/gemini/verve-organize-suprimentos', {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    },
    signal,
    body: JSON.stringify({ tsv, temperature: 0.25, max_tokens: 8192 }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    const rawMsg = String(json.message || '').trim();
    const busyLike =
      res.status === 429 ||
      res.status === 503 ||
      /busy|overloaded|high demand|too many requests|temporar|unavailable|indispon/i.test(rawMsg);
    const translatedMsg = busyLike
      ? 'A Orya está com alta demanda no momento. Tente novamente em alguns instantes.'
      : (rawMsg || `Erro ${res.status}`);
    const err = new Error(translatedMsg);
    err.status = res.status;
    err.code = json.code;
    throw err;
  }
  return json;
}

/** Primeiro objeto JSON com chaves balanceadas (respeita strings). */
function extractFirstJsonObject(str) {
  const start = str.indexOf('{');
  if (start < 0) return null;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < str.length; i += 1) {
    const ch = str[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (ch === '\\' && inString) {
      escape = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (ch === '{') depth += 1;
    if (ch === '}') {
      depth -= 1;
      if (depth === 0) return str.slice(start, i + 1);
    }
  }
  return null;
}

function tryFixTrailingCommas(jsonStr) {
  return jsonStr.replace(/,\s*([}\]])/g, '$1');
}

function normalizeVerveApiObject(o) {
  if (!o || typeof o !== 'object') return null;
  const produtosRaw = o.produtos ?? o.Produtos ?? o.items ?? o.dados ?? o.data;
  if (!Array.isArray(produtosRaw)) return null;
  const analise = String(
    o.analise ?? o.Analise ?? o.analysis ?? o.resumo ?? o.Resumo ?? ''
  ).trim();
  const produtos = produtosRaw.map((p) => ({
    codigo: String(p?.codigo ?? p?.Codigo ?? p?.Código ?? '').trim(),
    produto: String(
      p?.produto ?? p?.Produto ?? p?.nome ?? p?.Nome ?? p?.nomeProduto ?? ''
    ).trim(),
    marca: String(p?.marca ?? p?.Marca ?? '').trim(),
    grupo: String(p?.grupo ?? p?.Grupo ?? '').trim(),
    subgrupo: String(
      p?.subgrupo ?? p?.Subgrupo ?? p?.SubGrupo ?? p?.subGrupo ?? ''
    ).trim(),
  }));
  return { analise, produtos };
}

function tryParseVerveJsonString(str) {
  if (!str || typeof str !== 'string') return null;
  let o;
  try {
    o = JSON.parse(str);
  } catch {
    try {
      o = JSON.parse(tryFixTrailingCommas(str));
    } catch {
      try {
        o = JSON.parse(tryFixTrailingCommas(tryFixTrailingCommas(str)));
      } catch {
        return null;
      }
    }
  }
  return normalizeVerveApiObject(o);
}

function parseVerveIaContent(raw) {
  if (raw == null || typeof raw !== 'string') return null;
  let s = raw.trim().replace(/^\uFEFF/, '');
  const tryCandidates = [];

  const fenceRe = /```(?:json)?\s*([\s\S]*?)```/gi;
  let fm;
  while ((fm = fenceRe.exec(s)) !== null) {
    tryCandidates.push(fm[1].trim());
  }

  const extracted = extractFirstJsonObject(s);
  if (extracted) tryCandidates.push(extracted);

  tryCandidates.push(s);

  for (const c of tryCandidates) {
    const norm = tryParseVerveJsonString(c);
    if (norm) return norm;
  }

  for (const c of tryCandidates) {
    const sub = extractFirstJsonObject(c);
    if (sub && sub !== c) {
      const norm = tryParseVerveJsonString(sub);
      if (norm) return norm;
    }
  }

  return null;
}

function mergeVerveSuggestions(atuais, parsed) {
  const byCod = new Map(
    (parsed.produtos || []).map((p) => [String(p.codigo || '').trim(), p])
  );
  return atuais.map((o) => {
    const c = String(o.codigo || '').trim();
    const s = byCod.get(c);
    if (!s) {
      return {
        codigo: c,
        produto: o.produto,
        marca: o.marca,
        grupo: o.grupo,
        subgrupo: o.subgrupo,
      };
    }
    return {
      codigo: c,
      produto: String(s.produto || o.produto || '').trim() || o.produto,
      marca: String(s.marca ?? o.marca ?? '').trim(),
      grupo: String(s.grupo ?? o.grupo ?? '').trim(),
      subgrupo: String(s.subgrupo ?? o.subgrupo ?? '').trim(),
    };
  });
}

function applyHeaderRowStyle(ws, colCount) {
  const headerStyle = {
    font: { bold: true },
    fill: { fgColor: { rgb: 'FFE8E8E8' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  };
  for (let c = 0; c < colCount; c += 1) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[addr]) ws[addr].s = headerStyle;
  }
}

function downloadVerveStructureXlsx({ analise, rows }) {
  const inferSupType = (r) => {
    const txt = `${r?.produto || ''} ${r?.grupo || ''} ${r?.subgrupo || ''}`.toUpperCase();
    // Color tem precedência para evitar marcar ambos.
    const isColor = /\bCOLOR(IDA|IDO)?\b|COLORIDO|COLOUR|COR\b|CMYK|4X?4|4\/0|4C/.test(txt);
    if (isColor) return { pb: 0, color: 1 };
    return { pb: 1, color: 0 };
  };

  const headers = ['Código', 'Produto', 'Marca', 'Grupo', 'Subgrupo', 'Preto e branco', 'Color'];
  const data = rows.map((r) => {
    const t = inferSupType(r);
    return [r.codigo, r.produto, r.marca, r.grupo, r.subgrupo, t.pb, t.color];
  });
  const wsData = XLSX.utils.aoa_to_sheet([headers, ...data]);
  wsData['!cols'] = headers.map(() => ({ wch: 22 }));
  applyHeaderRowStyle(wsData, headers.length);

  const wsAnalise = XLSX.utils.aoa_to_sheet([['Análise e sugestões'], [String(analise || '').trim() || '—']]);
  wsAnalise['!cols'] = [{ wch: 100 }];
  if (wsAnalise.A1) wsAnalise.A1.s = { font: { bold: true }, fill: { fgColor: { rgb: 'FFE8E8E8' } } };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsData, 'Estrutura sugerida');
  XLSX.utils.book_append_sheet(wb, wsAnalise, 'Análise');
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `verve-estrutura-sugerida-${stamp}.xlsx`, { cellStyles: true });
}

export default function PermissoesCustomersView() {
  const DEFAULT_ROW_HEIGHT = 24;
  const OVERSCAN = 10;
  const [permTab, setPermTab] = useState('suprimentos');
  const [equipSearch, setEquipSearch] = useState('');
  const [equipSearchDeb, setEquipSearchDeb] = useState('');
  const [clientesSearch, setClientesSearch] = useState('');
  const [clientesSearchDeb, setClientesSearchDeb] = useState('');
  const [clientesTipoOpen, setClientesTipoOpen] = useState(false);
  const [clientesTiposSel, setClientesTiposSel] = useState(['LOCAÇÃO']);
  const [verveIaPhase, setVerveIaPhase] = useState('idle');
  const [verveIaResult, setVerveIaResult] = useState(null);
  const [verveIaError, setVerveIaError] = useState('');
  const [oryaIntroOpen, setOryaIntroOpen] = useState(false);
  const [verveLoadingMsgIdx, setVerveLoadingMsgIdx] = useState(0);
  /** Modal informativo (cota, validação) — substitui alert do navegador */
  const [verveInfoModal, setVerveInfoModal] = useState(null);

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMatrix, setImportMatrix] = useState(null);
  const [importValidation, setImportValidation] = useState(null);
  const [importDragOver, setImportDragOver] = useState(false);
  const [importProcessing, setImportProcessing] = useState(false);
  const [importResultMsg, setImportResultMsg] = useState(null);
  const importFileInputRef = useRef(null);

  const [equipImageMap, setEquipImageMap] = useState({});
  const [equipPhotoModal, setEquipPhotoModal] = useState(null);

  const [rows, setRows] = useState([]);
  const [clientesRows, setClientesRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [clientesLoading, setClientesLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [clientesError, setClientesError] = useState(null);
  const [toggleBusy, setToggleBusy] = useState('');
  const [clienteSortConfig, setClienteSortConfig] = useState({ key: 'cliente', direction: 'asc' });
  const [clienteBusyKey, setClienteBusyKey] = useState('');
  const [bloqFinPop, setBloqFinPop] = useState(null);
  const bloqFinAnchorRef = useRef(null);
  const [pedSupPop, setPedSupPop] = useState(null);
  const pedSupAnchorRef = useRef(null);
  const [chamadoTecModal, setChamadoTecModal] = useState(null);
  const [techCfgExport, setTechCfgExport] = useState(null);
  const [techCfgExportSel, setTechCfgExportSel] = useState([]);
  const [techCfgExportBusy, setTechCfgExportBusy] = useState(false);
  const [techCfgExportDone, setTechCfgExportDone] = useState(null);
  const clientesTipoRef = useRef(null);
  const [clientesGrupoSel, setClientesGrupoSel] = useState([]);
  const [clientesGrupoFilterOpen, setClientesGrupoFilterOpen] = useState(false);
  const clientesGrupoFilterWrapRef = useRef(null);
  const [grupoBulkOpen, setGrupoBulkOpen] = useState(false);
  const [grupoBulkBusy, setGrupoBulkBusy] = useState(false);
  const [grupoBulkDraft, setGrupoBulkDraft] = useState({
    opt_contador: false,
    opt_cob_os: false,
    opt_cob_os_dias: '',
    opt_cob_sup: false,
    opt_cob_sup_dias: '',
    opt_order_qty: false,
    opt_order_rma: false,
    opt_order_group: false,
  });
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [draggingBulk, setDraggingBulk] = useState(false);
  const [bulkDropHover, setBulkDropHover] = useState(null);
  const [bulkFeedback, setBulkFeedback] = useState(null);
  const selectedCodesRef = useRef([]);
  const bulkDragCodesRef = useRef([]);
  const [sortConfig, setSortConfig] = useState({ key: 'qtde', direction: 'desc' });
  const [columnFilters, setColumnFilters] = useState({
    marca: [],
    grupo: [],
    subgrupo: [],
  });
  const [openFilterCol, setOpenFilterCol] = useState('');
  const [qtdeTipOpen, setQtdeTipOpen] = useState(false);
  const marcaFilterAnchorRef = useRef(null);
  const grupoFilterAnchorRef = useRef(null);
  const subgrupoFilterAnchorRef = useRef(null);
  const qtdeTipRef = useRef(null);
  const tableScrollRef = useRef(null);
  const tableBodyRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(520);
  const [rowHeight, setRowHeight] = useState(DEFAULT_ROW_HEIGHT);
  const tableRows = useMemo(() => rows || [], [rows]);

  useEffect(() => {
    if (!qtdeTipOpen) return undefined;
    const onDocClick = (e) => {
      if (!qtdeTipRef.current?.contains(e.target)) setQtdeTipOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [qtdeTipOpen]);

  useEffect(() => {
    if (!clientesTipoOpen) return undefined;
    const onDocClick = (e) => {
      if (!clientesTipoRef.current?.contains(e.target)) setClientesTipoOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [clientesTipoOpen]);

  useEffect(() => {
    const t = setTimeout(() => setEquipSearchDeb(equipSearch), 280);
    return () => clearTimeout(t);
  }, [equipSearch]);

  useEffect(() => {
    const t = setTimeout(() => setClientesSearchDeb(clientesSearch), 280);
    return () => clearTimeout(t);
  }, [clientesSearch]);

  useEffect(() => {
    if (verveIaPhase !== 'loading') return undefined;
    const t = setInterval(() => setVerveLoadingMsgIdx((i) => (i === 0 ? 1 : 0)), 4500);
    return () => clearInterval(t);
  }, [verveIaPhase]);

  const loadEquipImageFlags = useCallback(async (codigos) => {
    const uniq = [...new Set((codigos || []).map((c) => String(c || '').trim()).filter(Boolean))];
    if (!uniq.length) {
      setEquipImageMap({});
      return;
    }
    try {
      const q = encodeURIComponent(uniq.join(','));
      const j = await apiVc(`/permissoes-sup/equip-imagens?codigos=${q}`);
      setEquipImageMap(j.data && typeof j.data === 'object' ? j.data : {});
    } catch {
      setEquipImageMap({});
    }
  }, []);

  const uploadEquipPhoto = useCallback(async (codigo, file) => {
    const code = String(codigo || '').trim();
    const presign = await apiVc(`/permissoes-sup/equip-imagem/${encodeURIComponent(code)}/presign`, {
      method: 'POST',
      body: {
        fileName: file.name,
        mimeType: file.type || 'image/jpeg',
        sizeBytes: file.size,
      },
    });
    const { uploadUrl, key, mime } = presign.data;
    const uploadRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': mime || file.type },
      body: file,
    });
    if (!uploadRes.ok) throw new Error('Falha ao enviar imagem para o armazenamento');
    await apiVc(`/permissoes-sup/equip-imagem/${encodeURIComponent(code)}/complete`, {
      method: 'POST',
      body: {
        s3Key: key,
        fileName: file.name,
        mimeType: file.type || mime,
        sizeBytes: file.size,
      },
    });
    setEquipImageMap((prev) => ({ ...prev, [code]: true }));
  }, []);

  const openEquipPhotoUpload = useCallback((row) => {
    const codigo = String(row?.codigo || '').trim();
    if (!codigo) return;
    setEquipPhotoModal({
      mode: 'upload',
      codigo,
      produtoNome: String(row?.nomeProduto || '').trim(),
    });
  }, []);

  const openEquipPhotoView = useCallback(async (row) => {
    const codigo = String(row?.codigo || '').trim();
    if (!codigo) return;
    try {
      const j = await apiVc(`/permissoes-sup/equip-imagem/${encodeURIComponent(codigo)}`);
      setEquipPhotoModal({
        mode: 'view',
        codigo,
        produtoNome: String(row?.nomeProduto || '').trim(),
        viewUrl: j.data?.viewUrl || '',
      });
    } catch (e) {
      setVerveInfoModal({
        title: 'Imagem indisponível',
        message: e.message || 'Não foi possível carregar a imagem.',
        variant: 'error',
      });
    }
  }, []);

  const loadRows = useCallback(async ({ silent } = {}) => {
    if (!silent) {
      setListLoading(true);
      setListError(null);
    }
    try {
      const q = encodeURIComponent(equipSearchDeb.trim());
      const j = await apiVc(`/permissoes-sup/lista-geral?q=${q}`);
      setRows(Array.isArray(j.data) ? j.data : []);
    } catch (e) {
      setRows([]);
      if (!silent) setListError(e.message || 'Falha ao carregar');
    } finally {
      if (!silent) setListLoading(false);
    }
  }, [equipSearchDeb]);

  const loadClientesRows = useCallback(async ({ silent } = {}) => {
    if (!silent) {
      setClientesLoading(true);
      setClientesError(null);
    }
    try {
      const q = encodeURIComponent(clientesSearchDeb.trim());
      const j = await apiVc(`/permissoes-cli/lista-geral?q=${q}`);
      const data = Array.isArray(j.data) ? j.data : [];
      setClientesRows(data);
    } catch (e) {
      setClientesRows([]);
      if (!silent) setClientesError(e.message || 'Falha ao carregar');
    } finally {
      if (!silent) setClientesLoading(false);
    }
  }, [clientesSearchDeb]);

  useEffect(() => {
    if (permTab !== 'suprimentos') return;
    loadRows();
  }, [permTab, loadRows]);

  useEffect(() => {
    if (permTab !== 'clientes') return;
    loadClientesRows();
  }, [permTab, loadClientesRows]);

  useEffect(() => {
    selectedCodesRef.current = selectedCodes;
  }, [selectedCodes]);

  useEffect(() => {
    const valid = new Set(tableRows.map((r) => String(r.codigo || '').trim()).filter(Boolean));
    setSelectedCodes((prev) => prev.filter((c) => valid.has(c)));
  }, [tableRows]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setSelectedCodes([]);
        setDraggingBulk(false);
        setBulkDropHover(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!bulkFeedback || bulkFeedback.phase === 'loading') return undefined;
    const t = setTimeout(() => setBulkFeedback(null), 4500);
    return () => clearTimeout(t);
  }, [bulkFeedback]);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const updateSize = () => setViewportHeight(el.clientHeight || 520);
    updateSize();
    const ro = new ResizeObserver(updateSize);
    ro.observe(el);
    return () => ro.disconnect();
  }, [permTab]);

  useEffect(() => {
    if (!tableBodyRef.current) return;
    const sampleRow = tableBodyRef.current.querySelector('tr.perm-tr--model');
    if (!sampleRow) return;
    const h = Math.ceil(sampleRow.getBoundingClientRect().height);
    if (Number.isFinite(h) && h > 0) setRowHeight(h);
  }, [tableRows.length, viewportHeight, listLoading]);

  const toggleTypeSup = useCallback(
    async (codprod, typesup) => {
      const codigo = String(codprod || '').trim();
      if (!codigo) return;
      const cleared = typesup == null || String(typesup).trim() === '';
      const tpApi = cleared ? null : String(typesup).trim().toLowerCase();
      const busyKey = cleared ? `${codigo}:clear` : `${codigo}:${tpApi}`;
      setToggleBusy(busyKey);
      const prev = rows;
      setRows((curr) =>
        curr.map((r) => (String(r.codigo) === codigo ? { ...r, typesup: cleared ? '' : tpApi } : r))
      );
      try {
        await apiVc('/permissoes-sup/tipo', {
          method: 'PATCH',
          body: { codprod: codigo, typesup: tpApi },
        });
      } catch (e) {
        setRows(prev);
        window.alert(e.message || 'Erro ao atualizar');
      } finally {
        setToggleBusy('');
      }
    },
    [rows]
  );

  const selectedSet = useMemo(() => new Set(selectedCodes), [selectedCodes]);
  const hasSelection = selectedCodes.length > 0;

  const toggleRowSelected = useCallback((codigo) => {
    const c = String(codigo || '').trim();
    if (!c) return;
    setSelectedCodes((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  }, []);

  const handleBulkDrop = useCallback(
    async (typesup) => {
      const codes = [...bulkDragCodesRef.current];
      if (!codes.length) return;
      setDraggingBulk(false);
      setBulkDropHover(null);
      setBulkFeedback({ phase: 'loading', count: codes.length });
      try {
        const j = await apiVc('/permissoes-sup/tipo-bulk', {
          method: 'POST',
          body: { codprods: codes, typesup },
        });
        const n = j.data?.updated ?? 0;
        setBulkFeedback({ phase: 'success', count: n, typesup });
        setSelectedCodes([]);
        await loadRows({ silent: true });
      } catch (err) {
        setBulkFeedback({
          phase: 'error',
          message: err.message || 'Erro ao aplicar',
        });
      }
    },
    [loadRows]
  );

  const onBulkDragStart = useCallback((e) => {
    const codes = [...selectedCodesRef.current];
    if (!codes.length) {
      e.preventDefault();
      return;
    }
    bulkDragCodesRef.current = codes;
    setDraggingBulk(true);
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', 'perm-bulk');
  }, []);

  const onBulkDragEnd = useCallback(() => {
    setDraggingBulk(false);
    setBulkDropHover(null);
  }, []);

  const filterOptions = useMemo(() => {
    const baseFiltered = (excludeCol) => {
      let data = [...tableRows];
      if (excludeCol !== 'marca' && columnFilters.marca.length) {
        const f = new Set(columnFilters.marca);
        data = data.filter((r) => f.has(String(r.marca || '').trim()));
      }
      if (excludeCol !== 'grupo' && columnFilters.grupo.length) {
        const f = new Set(columnFilters.grupo);
        data = data.filter((r) => f.has(String(r.grupo || '').trim()));
      }
      if (excludeCol !== 'subgrupo' && columnFilters.subgrupo.length) {
        const f = new Set(columnFilters.subgrupo);
        data = data.filter((r) => f.has(String(r.subgrupo || '').trim()));
      }
      return data;
    };
    const valuesFor = (key, excludeCol) => {
      const vals = [...new Set(baseFiltered(excludeCol).map((r) => String(r[key] || '').trim()).filter(Boolean))];
      return vals.sort((a, b) => a.localeCompare(b, 'pt-BR'));
    };
    return {
      marca: valuesFor('marca', 'marca'),
      grupo: valuesFor('grupo', 'grupo'),
      subgrupo: valuesFor('subgrupo', 'subgrupo'),
    };
  }, [tableRows, columnFilters]);

  useEffect(() => {
    setColumnFilters((prev) => {
      const prune = (col) => {
        const allowed = new Set(filterOptions[col] || []);
        return (prev[col] || []).filter((v) => allowed.has(v));
      };
      const next = {
        marca: prune('marca'),
        grupo: prune('grupo'),
        subgrupo: prune('subgrupo'),
      };
      const same =
        next.marca.length === prev.marca.length &&
        next.grupo.length === prev.grupo.length &&
        next.subgrupo.length === prev.subgrupo.length &&
        next.marca.every((v, i) => v === prev.marca[i]) &&
        next.grupo.every((v, i) => v === prev.grupo[i]) &&
        next.subgrupo.every((v, i) => v === prev.subgrupo[i]);
      return same ? prev : next;
    });
  }, [filterOptions]);

  const processedRows = useMemo(() => {
    let data = [...tableRows];
    if (columnFilters.marca.length) {
      const f = new Set(columnFilters.marca);
      data = data.filter((r) => f.has(String(r.marca || '').trim()));
    }
    if (columnFilters.grupo.length) {
      const f = new Set(columnFilters.grupo);
      data = data.filter((r) => f.has(String(r.grupo || '').trim()));
    }
    if (columnFilters.subgrupo.length) {
      const f = new Set(columnFilters.subgrupo);
      data = data.filter((r) => f.has(String(r.subgrupo || '').trim()));
    }

    const { key, direction } = sortConfig;
    const mult = direction === 'asc' ? 1 : -1;
    data.sort((a, b) => {
      if (key === 'qtde') {
        const an = Number(a.qtde) || 0;
        const bn = Number(b.qtde) || 0;
        return (an - bn) * mult;
      }
      const av = String(a[key] ?? '').trim().toLowerCase();
      const bv = String(b[key] ?? '').trim().toLowerCase();
      return av.localeCompare(bv, 'pt-BR', { numeric: true }) * mult;
    });
    return data;
  }, [tableRows, columnFilters, sortConfig]);

  const visibleImportCodes = useMemo(() => {
    const s = new Set();
    for (const r of processedRows) {
      const c = normalizeImportCod5(r.codigo);
      if (c) s.add(c);
    }
    return s;
  }, [processedRows]);

  const totalRows = processedRows.length;
  const visibleCount = Math.max(1, Math.ceil(viewportHeight / rowHeight));
  const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN);
  const endIndex = Math.min(totalRows, startIndex + visibleCount + OVERSCAN * 2);
  const topSpacerHeight = startIndex * rowHeight;
  const bottomSpacerHeight = Math.max(0, (totalRows - endIndex) * rowHeight);
  const virtualRows = processedRows.slice(startIndex, endIndex);

  useEffect(() => {
    if (permTab !== 'suprimentos') return;
    const codes = processedRows.map((r) => String(r.codigo || '').trim()).filter(Boolean);
    loadEquipImageFlags(codes);
  }, [permTab, processedRows, loadEquipImageFlags]);

  const toggleSort = useCallback((key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  }, []);

  const sortedClientesRows = useMemo(() => {
    const data = [...clientesRows];
    const { key, direction } = clienteSortConfig;
    const mult = direction === 'asc' ? 1 : -1;
    data.sort((a, b) => {
      const av = String(a[key] ?? '').trim().toLowerCase();
      const bv = String(b[key] ?? '').trim().toLowerCase();
      return av.localeCompare(bv, 'pt-BR', { numeric: true }) * mult;
    });
    return data;
  }, [clientesRows, clienteSortConfig]);

  const clientesTipoOptions = useMemo(() => {
    return [...new Set(clientesRows.map((r) => String(r.tipo || '').trim()).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, 'pt-BR')
    );
  }, [clientesRows]);

  const clientesRowsAfterTipo = useMemo(() => {
    if (!clientesTiposSel.length) return [];
    const allowed = new Set(clientesTiposSel);
    return sortedClientesRows.filter((r) => allowed.has(String(r.tipo || '').trim()));
  }, [sortedClientesRows, clientesTiposSel]);

  const clientesGrupoOptions = useMemo(() => {
    const set = new Set();
    for (const r of clientesRowsAfterTipo) {
      set.add(normClienteGrupoKey(r));
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [clientesRowsAfterTipo]);

  useEffect(() => {
    const allowed = new Set(clientesGrupoOptions);
    setClientesGrupoSel((prev) => prev.filter((g) => allowed.has(g)));
  }, [clientesGrupoOptions]);

  const hasActiveClientesGrupoFilter = useMemo(
    () =>
      clientesGrupoSel.length > 0 &&
      clientesGrupoSel.length < clientesGrupoOptions.length &&
      clientesGrupoOptions.length > 1,
    [clientesGrupoSel, clientesGrupoOptions]
  );

  const showAjustarGrupoBtn = hasActiveClientesGrupoFilter && clientesGrupoSel.length === 1;

  const filteredClientesRows = useMemo(() => {
    if (!clientesRowsAfterTipo.length) return [];
    if (!clientesGrupoSel.length) return clientesRowsAfterTipo;
    const gset = new Set(clientesGrupoSel);
    return clientesRowsAfterTipo.filter((r) => gset.has(normClienteGrupoKey(r)));
  }, [clientesRowsAfterTipo, clientesGrupoSel]);

  const toggleClienteSort = useCallback((key) => {
    setClienteSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  }, []);

  const saveClienteRow = useCallback(
    async (row, nextPatch) => {
      const key = `${row.codigo}::${row.contrato}`;
      setClienteBusyKey(key);
      try {
        await apiVc('/permissoes-cli/tipo', {
          method: 'PATCH',
          body: {
            codcli: row.codigo,
            contrato: row.contrato,
            opt_contador: row.opt_contador,
            opt_cob_os: row.opt_cob_os,
            opt_cob_os_dias: row.opt_cob_os_dias,
            opt_cob_sup: row.opt_cob_sup,
            opt_cob_sup_dias: row.opt_cob_sup_dias,
            opt_order_qty: row.opt_order_qty,
            opt_order_rma: row.opt_order_rma,
            opt_order_group: row.opt_order_group,
            tech_call_cfg: row.tech_call_cfg ?? null,
            ...nextPatch,
          },
        });
        await loadClientesRows({ silent: true });
        setBloqFinPop(null);
        setChamadoTecModal((prev) => {
          if (!prev) return prev;
          const has = (k) => !!nextPatch && Object.prototype.hasOwnProperty.call(nextPatch, k);
          const cfg = has('tech_call_cfg') ? nextPatch.tech_call_cfg : prev.tech_call_cfg;
          const ov = has('overview_visibility') ? nextPatch.overview_visibility : prev.overview_visibility;
          const startDates = {};
          ['os_start_date', 'supply_order_start_date', 'quality_rating_start_date'].forEach((k) => {
            if (has(k)) startDates[k] = nextPatch[k];
          });
          return {
            ...prev,
            tech_call_cfg: cfg,
            overview_visibility: ov,
            row: prev.row
              ? { ...prev.row, tech_call_cfg: cfg, overview_visibility: ov, ...startDates }
              : prev.row,
          };
        });
      } catch (e) {
        const msg = e.message || 'Erro ao atualizar';
        if (/Agrupar Pedido|"(por site) Agrupado"|Agrupado/i.test(msg)) {
          setVerveInfoModal({
            variant: 'info',
            title: 'Não é possível habilitar Agrupar Pedido',
            message: msg,
            hints: ORDER_GROUP_CFG_WHERE_HINTS,
          });
        } else {
          window.alert(msg);
        }
      } finally {
        setClienteBusyKey('');
      }
    },
    [loadClientesRows]
  );

  const ensureOrderGroupCfgReady = useCallback(async () => {
    try {
      const ped = await fetchPedStatusConfig();
      const missing = getMissingOrderGroupCfgFields(ped);
      if (!missing.length) return true;
      setVerveInfoModal({
        variant: 'info',
        title: 'Configure a aba Agrupado antes de habilitar',
        message:
          'Para ligar Agrupar Pedido, a configuração “(por site) Agrupado” precisa estar completa. Campos pendentes:',
        missing,
        hints: ORDER_GROUP_CFG_WHERE_HINTS,
      });
      return false;
    } catch (e) {
      setVerveInfoModal({
        variant: 'error',
        title: 'Não foi possível validar a configuração',
        message: e.message || 'Falha ao carregar a configuração de abertura de pedido.',
      });
      return false;
    }
  }, []);

  const updateClienteBit = useCallback(
    async (row, field, checked) => {
      if (field === 'opt_order_group' && checked) {
        const ok = await ensureOrderGroupCfgReady();
        if (!ok) return;
      }
      const val = checked ? 1 : 0;
      const patch = { [field]: val };
      if (field === 'opt_cob_os' && !checked) patch.opt_cob_os_dias = null;
      if (field === 'opt_cob_sup' && !checked) patch.opt_cob_sup_dias = null;
      await saveClienteRow(row, patch);
    },
    [saveClienteRow, ensureOrderGroupCfgReady]
  );

  const openBloqFinPop = useCallback((e, row) => {
    bloqFinAnchorRef.current = e.currentTarget;
    setBloqFinPop({
      rowKey: `${row.codigo}::${row.contrato}`,
      draft: {
        os: row.opt_cob_os === 1,
        osDias: row.opt_cob_os_dias == null ? '' : String(row.opt_cob_os_dias),
        sup: row.opt_cob_sup === 1,
        supDias: row.opt_cob_sup_dias == null ? '' : String(row.opt_cob_sup_dias),
      },
    });
  }, []);

  const closeBloqFinPop = useCallback(() => setBloqFinPop(null), []);

  const confirmBloqFinPop = useCallback(async () => {
    if (!bloqFinPop) return;
    const popRow = filteredClientesRows.find(
      (r) => `${r.codigo}::${r.contrato}` === bloqFinPop.rowKey
    );
    if (!popRow) {
      setBloqFinPop(null);
      return;
    }
    const d = bloqFinPop.draft;
    let opt_cob_os_dias = null;
    let opt_cob_sup_dias = null;
    if (d.os) {
      const days = String(d.osDias || '').trim();
      if (!days) {
        window.alert('Informe os dias de Bloqueio OS.');
        return;
      }
      const n = Number(days);
      if (!Number.isInteger(n) || n < 0 || n > 999) {
        window.alert('Dias de Bloqueio OS: use um número inteiro de 0 a 999.');
        return;
      }
      opt_cob_os_dias = n;
    }
    if (d.sup) {
      const days = String(d.supDias || '').trim();
      if (!days) {
        window.alert('Informe os dias de Bloqueio Pedido.');
        return;
      }
      const n = Number(days);
      if (!Number.isInteger(n) || n < 0 || n > 999) {
        window.alert('Dias de Bloqueio Pedido: use um número inteiro de 0 a 999.');
        return;
      }
      opt_cob_sup_dias = n;
    }
    await saveClienteRow(popRow, {
      opt_cob_os: d.os ? 1 : 0,
      opt_cob_os_dias,
      opt_cob_sup: d.sup ? 1 : 0,
      opt_cob_sup_dias,
    });
    setBloqFinPop(null);
  }, [bloqFinPop, filteredClientesRows, saveClienteRow]);

  const openPedSupPop = useCallback((e, row) => {
    pedSupAnchorRef.current = e.currentTarget;
    setPedSupPop({
      rowKey: `${row.codigo}::${row.contrato}`,
      draft: {
        contador: row.opt_contador === 1,
        orderQty: row.opt_order_qty === 1,
        orderRma: row.opt_order_rma === 1,
        orderGroup: row.opt_order_group === 1,
      },
    });
  }, []);

  const closePedSupPop = useCallback(() => setPedSupPop(null), []);

  const confirmPedSupPop = useCallback(async () => {
    if (!pedSupPop) return;
    const popRow = filteredClientesRows.find(
      (r) => `${r.codigo}::${r.contrato}` === pedSupPop.rowKey
    );
    if (!popRow) {
      setPedSupPop(null);
      return;
    }
    const d = pedSupPop.draft;
    if (d.orderGroup && popRow.opt_order_group !== 1) {
      const ok = await ensureOrderGroupCfgReady();
      if (!ok) return;
    }
    await saveClienteRow(popRow, {
      opt_contador: d.contador ? 1 : 0,
      opt_order_qty: d.orderQty ? 1 : 0,
      opt_order_rma: d.orderRma ? 1 : 0,
      opt_order_group: d.orderGroup ? 1 : 0,
    });
    setPedSupPop(null);
  }, [pedSupPop, filteredClientesRows, saveClienteRow, ensureOrderGroupCfgReady]);

  const openGrupoBulkModal = useCallback(() => {
    setGrupoBulkDraft({
      opt_contador: false,
      opt_cob_os: false,
      opt_cob_os_dias: '',
      opt_cob_sup: false,
      opt_cob_sup_dias: '',
      opt_order_qty: false,
      opt_order_rma: false,
      opt_order_group: false,
    });
    setGrupoBulkOpen(true);
  }, []);

  const confirmGrupoBulk = useCallback(async () => {
    if (!filteredClientesRows.length) {
      window.alert('Nenhum contrato na listagem atual.');
      return;
    }
    const opt_contador = grupoBulkDraft.opt_contador ? 1 : 0;
    const opt_cob_os = grupoBulkDraft.opt_cob_os ? 1 : 0;
    const opt_cob_sup = grupoBulkDraft.opt_cob_sup ? 1 : 0;
    const opt_order_qty = grupoBulkDraft.opt_order_qty ? 1 : 0;
    const opt_order_rma = grupoBulkDraft.opt_order_rma ? 1 : 0;
    const opt_order_group = grupoBulkDraft.opt_order_group ? 1 : 0;
    let opt_cob_os_dias = null;
    let opt_cob_sup_dias = null;
    if (opt_cob_os) {
      const d = String(grupoBulkDraft.opt_cob_os_dias || '').trim();
      if (!d) {
        window.alert('Informe os dias de Bloqueio OS.');
        return;
      }
      const n = Number(d);
      if (!Number.isInteger(n) || n < 0 || n > 999) {
        window.alert('Dias de Bloqueio OS: use um número inteiro de 0 a 999.');
        return;
      }
      opt_cob_os_dias = n;
    }
    if (opt_cob_sup) {
      const d = String(grupoBulkDraft.opt_cob_sup_dias || '').trim();
      if (!d) {
        window.alert('Informe os dias de Bloqueio Pedido.');
        return;
      }
      const n = Number(d);
      if (!Number.isInteger(n) || n < 0 || n > 999) {
        window.alert('Dias de Bloqueio Pedido: use um número inteiro de 0 a 999.');
        return;
      }
      opt_cob_sup_dias = n;
    }
    if (opt_order_group) {
      const ok = await ensureOrderGroupCfgReady();
      if (!ok) return;
    }
    setGrupoBulkBusy(true);
    try {
      await apiVc('/permissoes-cli/bulk', {
        method: 'POST',
        body: {
          items: filteredClientesRows.map((r) => ({ codcli: r.codigo, contrato: r.contrato })),
          opt_contador,
          opt_cob_os,
          opt_cob_os_dias,
          opt_cob_sup,
          opt_cob_sup_dias,
          opt_order_qty,
          opt_order_rma,
          opt_order_group,
        },
      });
      setGrupoBulkOpen(false);
      await loadClientesRows({ silent: true });
    } catch (e) {
      const msg = e.message || 'Erro ao aplicar';
      if (/Agrupar Pedido|"(por site) Agrupado"|Agrupado/i.test(msg)) {
        setVerveInfoModal({
          variant: 'info',
          title: 'Não é possível habilitar Agrupar Pedido',
          message: msg,
          hints: ORDER_GROUP_CFG_WHERE_HINTS,
        });
      } else {
        window.alert(msg);
      }
    } finally {
      setGrupoBulkBusy(false);
    }
  }, [filteredClientesRows, grupoBulkDraft, loadClientesRows, ensureOrderGroupCfgReady]);

  const techCfgExportTargets = useMemo(() => {
    if (!techCfgExport) return [];
    return filteredClientesRows.filter(
      (r) => `${r.codigo}::${r.contrato}` !== techCfgExport.sourceKey
    );
  }, [techCfgExport, filteredClientesRows]);

  const techCfgExportSelSet = useMemo(() => new Set(techCfgExportSel), [techCfgExportSel]);

  const techCfgExportAllState = useMemo(() => {
    if (!techCfgExportTargets.length) return 'none';
    let n = 0;
    for (const r of techCfgExportTargets) {
      if (techCfgExportSelSet.has(`${r.codigo}::${r.contrato}`)) n += 1;
    }
    if (n === 0) return 'none';
    return n === techCfgExportTargets.length ? 'all' : 'some';
  }, [techCfgExportTargets, techCfgExportSelSet]);

  const startTechCfgExport = useCallback(() => {
    if (!chamadoTecModal?.row) return;
    const src = chamadoTecModal.row;
    setTechCfgExport({
      sourceKey: `${src.codigo}::${src.contrato}`,
      codigo: src.codigo,
      contrato: src.contrato,
      cliente: src.cliente,
    });
    setTechCfgExportSel([]);
    setTechCfgExportDone(null);
    setChamadoTecModal(null);
  }, [chamadoTecModal]);

  const cancelTechCfgExport = useCallback(() => {
    setTechCfgExport(null);
    setTechCfgExportSel([]);
  }, []);

  const toggleTechCfgExportRow = useCallback((key) => {
    setTechCfgExportSel((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }, []);

  const toggleTechCfgExportAll = useCallback(() => {
    const keys = techCfgExportTargets.map((r) => `${r.codigo}::${r.contrato}`);
    setTechCfgExportSel((prev) => {
      const prevSet = new Set(prev);
      const allOn = keys.length > 0 && keys.every((k) => prevSet.has(k));
      if (allOn) return prev.filter((k) => !keys.includes(k));
      return Array.from(new Set([...prev, ...keys]));
    });
  }, [techCfgExportTargets]);

  const confirmTechCfgExport = useCallback(async () => {
    if (!techCfgExport || !techCfgExportSel.length) return;
    setTechCfgExportBusy(true);
    try {
      const j = await apiVc('/permissoes-cli/tech-call-cfg/replicate', {
        method: 'POST',
        body: {
          source: { codcli: techCfgExport.codigo, contrato: techCfgExport.contrato },
          targets: techCfgExportSel.map((k) => {
            const [codcli, ...rest] = k.split('::');
            return { codcli, contrato: rest.join('::') };
          }),
        },
      });
      await loadClientesRows({ silent: true });
      setTechCfgExportDone(Number(j?.data?.updated) || techCfgExportSel.length);
      setTechCfgExport(null);
      setTechCfgExportSel([]);
    } catch (e) {
      window.alert(e.message || 'Erro ao replicar configuração');
    } finally {
      setTechCfgExportBusy(false);
    }
  }, [techCfgExport, techCfgExportSel, loadClientesRows]);

  useEffect(() => {
    if (techCfgExportDone == null) return undefined;
    const t = window.setTimeout(() => setTechCfgExportDone(null), 4000);
    return () => window.clearTimeout(t);
  }, [techCfgExportDone]);

  useEffect(() => {
    if (permTab !== 'clientes' && techCfgExport) cancelTechCfgExport();
  }, [permTab, techCfgExport, cancelTechCfgExport]);

  const hasActiveFilter = useCallback(
    (col) => {
      const sel = columnFilters[col] || [];
      const all = filterOptions[col] || [];
      return sel.length > 0 && sel.length < all.length;
    },
    [columnFilters, filterOptions]
  );

  /** Orya só com filtro parcial de Marca/Grupo/Subgrupo (não a listagem completa). */
  const canOrganizeWithOrya = useMemo(() => {
    const hasColFilter =
      hasActiveFilter('marca') || hasActiveFilter('grupo') || hasActiveFilter('subgrupo');
    if (!hasColFilter) return false;
    if (!tableRows.length || !processedRows.length) return false;
    return processedRows.length < tableRows.length;
  }, [hasActiveFilter, tableRows.length, processedRows.length]);

  const oryaOrganizeDisabledReason = !canOrganizeWithOrya
    ? 'Aplique um filtro de Marca, Grupo ou Subgrupo que reduza a listagem (não todos os produtos).'
    : '';

  const runVerveOrganize = useCallback(async () => {
    if (!canOrganizeWithOrya) {
      setVerveInfoModal({
        variant: 'info',
        title: 'Filtro necessário',
        message:
          'Para usar a Orya, aplique um filtro de Marca, Grupo ou Subgrupo que não retorne todos os produtos da tabela.',
      });
      return;
    }
    if (!processedRows.length) {
      setVerveInfoModal({
        variant: 'info',
        title: 'Nada para analisar',
        message:
          'Não há linhas na tabela com os filtros atuais. Ajuste a busca ou os filtros de coluna e tente novamente.',
      });
      return;
    }
    setVerveLoadingMsgIdx(0);
    setVerveIaError('');
    setVerveIaResult(null);
    setVerveIaPhase('loading');
    const produtosAtuais = processedRows.map((r) => ({
      codigo: String(r.codigo || '').trim(),
      produto: String(r.nomeProduto || '').trim(),
      marca: String(r.marca || '').trim(),
      grupo: String(r.grupo || '').trim(),
      subgrupo: String(r.subgrupo || '').trim(),
    }));
    const userPayload = buildVerveProdutosTsv(produtosAtuais);
    try {
      const j = await apiGeminiVerveOrganizeSuprimentos(userPayload, undefined);
      const parsed = parseVerveIaContent(j.content);
      if (!parsed) {
        throw new Error('Resposta da IA em formato inválido. Tente novamente.');
      }
      const merged = mergeVerveSuggestions(produtosAtuais, parsed);
      const analise = String(parsed.analise || '').trim() || 'Análise concluída.';
      setVerveIaResult({ analise, rows: merged });
      setVerveIaPhase('result');
    } catch (e) {
      setVerveIaError(e.message || 'Falha na análise');
      setVerveIaPhase('error');
    }
  }, [canOrganizeWithOrya, processedRows]);

  const closeVerveModal = useCallback(() => {
    setVerveIaPhase('idle');
    setVerveIaResult(null);
    setVerveIaError('');
  }, []);

  const closeVerveInfoModal = useCallback(() => setVerveInfoModal(null), []);

  const closeImportModal = useCallback(() => {
    setImportModalOpen(false);
    setImportFile(null);
    setImportMatrix(null);
    setImportValidation(null);
    setImportDragOver(false);
    setImportProcessing(false);
    setImportResultMsg(null);
    if (importFileInputRef.current) importFileInputRef.current.value = '';
  }, []);

  const loadImportFile = useCallback(async (file) => {
    if (!isXlsxFile(file)) {
      setImportFile(null);
      setImportMatrix(null);
      setImportValidation({
        ok: false,
        errors: ['Selecione apenas arquivo Excel (.xlsx).'],
        items: [],
      });
      return;
    }
    setImportFile(file);
    setImportResultMsg(null);
    try {
      const matrix = await readXlsxAsMatrix(file);
      setImportMatrix(matrix);
    } catch (e) {
      setImportMatrix(null);
      setImportValidation({
        ok: false,
        errors: [e.message || 'Erro ao ler a planilha.'],
        items: [],
      });
    }
  }, []);

  const clearImportFile = useCallback(() => {
    setImportFile(null);
    setImportMatrix(null);
    setImportValidation(null);
    setImportResultMsg(null);
    if (importFileInputRef.current) importFileInputRef.current.value = '';
  }, []);

  useEffect(() => {
    if (!importModalOpen || !importMatrix) return;
    setImportValidation(validateImportSheetRows(importMatrix, visibleImportCodes));
  }, [importModalOpen, importMatrix, visibleImportCodes]);

  const runImportProcess = useCallback(async () => {
    if (!importValidation?.ok || !importValidation.items?.length) return;
    setImportProcessing(true);
    setImportResultMsg(null);
    try {
      const j = await apiVc('/permissoes-sup/import-planilha', {
        method: 'POST',
        body: { items: importValidation.items },
      });
      const applied = j.data?.applied ?? 0;
      const skipped = j.data?.skippedExisting ?? 0;
      let msg = '';
      if (applied > 0 && skipped > 0) {
        msg = `${applied} novo(s) gravado(s). ${skipped} ignorado(s) (já existiam na base).`;
      } else if (applied > 0) {
        msg = `${applied} produto(s) gravado(s) com sucesso.`;
      } else if (skipped > 0) {
        msg = `Nenhum novo registro: ${skipped} código(s) já cadastrados foram ignorados.`;
      } else {
        msg = 'Processamento concluído.';
      }
      setImportResultMsg(msg);
      await loadRows({ silent: true });
      setImportProcessing(false);
      setTimeout(() => closeImportModal(), 1400);
    } catch (e) {
      setImportProcessing(false);
      setImportValidation((prev) => ({
        ok: false,
        errors: [e.message || 'Falha ao gravar.'],
        items: prev?.items || [],
      }));
    }
  }, [importValidation, loadRows, closeImportModal]);

  useEffect(() => {
    if (!verveInfoModal) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeVerveInfoModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [verveInfoModal, closeVerveInfoModal]);

  useEffect(() => {
    if (verveIaPhase !== 'result' && verveIaPhase !== 'error') return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeVerveModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [verveIaPhase, closeVerveModal]);

  useEffect(() => {
    if (!oryaIntroOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOryaIntroOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [oryaIntroOpen]);

  const onDownloadVerveXlsx = useCallback(() => {
    if (!verveIaResult?.rows?.length) return;
    downloadVerveStructureXlsx({
      analise: verveIaResult.analise,
      rows: verveIaResult.rows,
    });
    closeVerveModal();
  }, [verveIaResult, closeVerveModal]);

  const verveBusy =
    listLoading || bulkFeedback?.phase === 'loading' || verveIaPhase === 'loading';

  const importBusy = importProcessing || listLoading;

  return (
    <div className="pcs2-page perm-customers-page">
      <section className="pcs2-section perm-customers-section">
        <div className="pcs2-section-aside">
          <div className="pcs2-section-heading">
            <FolderKey size={16} strokeWidth={1.75} aria-hidden />
            <h3 className="pcs2-section-title">Permissões</h3>
          </div>
          <p className="pcs2-section-desc">Gerencie permissões de suprimentos e clientes</p>
        </div>

        <div className="perm-customers-segment-bar">
          <div className="perm-segment" role="tablist" aria-label="Módulo permissões">
            <button
              type="button"
              role="tab"
              aria-selected={permTab === 'suprimentos'}
              className="perm-segment-btn"
              onClick={() => setPermTab('suprimentos')}
            >
              Suprimentos
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={permTab === 'clientes'}
              className="perm-segment-btn"
              onClick={() => setPermTab('clientes')}
            >
              Clientes
            </button>
          </div>
        </div>

        <div className="perm-customers-toolbar-bar">
          {permTab === 'clientes' && techCfgExport ? (
            <div className="perm-techcfg-export-bar" role="status">
              <span className="perm-techcfg-export-ico" aria-hidden>
                <IconShare3 size={14} stroke={1.9} />
              </span>
              <span className="perm-techcfg-export-text">
                Replicando configuração técnica <strong>“Dashboard”</strong> de{' '}
                <strong title={techCfgExport.cliente}>{techCfgExport.contrato}</strong>
                <span className="perm-techcfg-export-count">
                  {techCfgExportSel.length} selecionado{techCfgExportSel.length === 1 ? '' : 's'}
                </span>
              </span>
              <button
                type="button"
                className="user-form-link-btn perm-techcfg-export-cancel"
                onClick={cancelTechCfgExport}
                disabled={techCfgExportBusy}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="user-form-profile-save-btn perm-techcfg-export-confirm"
                onClick={confirmTechCfgExport}
                disabled={techCfgExportBusy || techCfgExportSel.length === 0}
              >
                {techCfgExportBusy ? (
                  <>
                    <Loader2 size={13} className="perm-bulk-spin" aria-hidden />
                    Replicando…
                  </>
                ) : (
                  'Confirmar replicação'
                )}
              </button>
            </div>
          ) : null}
          {permTab === 'clientes' && !techCfgExport && techCfgExportDone != null ? (
            <div className="perm-techcfg-export-bar perm-techcfg-export-bar--done" role="status">
              <CheckCircle2 size={14} aria-hidden />
              <span className="perm-techcfg-export-text">
                Configuração replicada para {techCfgExportDone} contrato
                {techCfgExportDone === 1 ? '' : 's'}
              </span>
            </div>
          ) : null}
          <div className="perm-customers-toolbar">
            {permTab === 'suprimentos' && (
              <>
                <BoardToolbarExpandSearch
                  forceExpanded
                  value={equipSearch}
                  onChange={setEquipSearch}
                  placeholder="Buscar código, produto, marca, grupo..."
                  ariaLabel="Buscar suprimentos"
                />
                <div className="perm-import-wrap">
                  <button
                    type="button"
                    className="board-clickup-filter-chip perm-toolbar-chip"
                    onClick={() => {
                      setImportModalOpen(true);
                      setImportFile(null);
                      setImportMatrix(null);
                      setImportValidation(null);
                      setImportResultMsg(null);
                      if (importFileInputRef.current) importFileInputRef.current.value = '';
                    }}
                    disabled={verveBusy || importBusy || !processedRows.length}
                  >
                    <span className="board-clickup-filter-chip-icon" aria-hidden>
                      <HardDriveUpload size={13} strokeWidth={1.5} />
                    </span>
                    <span className="board-clickup-filter-chip-text">
                      <span className="board-clickup-filter-chip-label">Importar atualização</span>
                    </span>
                  </button>
                </div>
                <div className="perm-agru-wrap">
                  <button
                    type="button"
                    className="board-clickup-filter-chip perm-toolbar-chip perm-toolbar-chip--orya"
                    onClick={() => setOryaIntroOpen(true)}
                    disabled={verveBusy || !canOrganizeWithOrya}
                    title={oryaOrganizeDisabledReason || undefined}
                    aria-busy={verveIaPhase === 'loading'}
                  >
                    <span className="board-clickup-filter-chip-icon" aria-hidden>
                      <IconSparkles size={13} stroke={1.75} className="perm-verve-brain-ico" />
                    </span>
                    <span className="board-clickup-filter-chip-text">
                      <span className="board-clickup-filter-chip-label">Organize com Orya</span>
                    </span>
                  </button>
                </div>
              </>
            )}
            {permTab === 'clientes' && (
              <>
                <BoardToolbarExpandSearch
                  forceExpanded
                  value={clientesSearch}
                  onChange={setClientesSearch}
                  placeholder="Buscar código, contrato, cliente, grupo..."
                  ariaLabel="Buscar clientes"
                />
                <div className="perm-clientes-filtro-tipo" ref={clientesTipoRef}>
                  <button
                    type="button"
                    className={`board-clickup-filter-chip perm-toolbar-chip${
                      clientesTiposSel.length ? ' has-selection' : ''
                    }`}
                    onClick={() => {
                      setClientesGrupoFilterOpen(false);
                      setClientesTipoOpen((v) => !v);
                    }}
                    aria-expanded={clientesTipoOpen}
                  >
                    <span className="board-clickup-filter-chip-icon" aria-hidden>
                      <Funnel size={13} strokeWidth={1.5} />
                    </span>
                    <span className="board-clickup-filter-chip-text">
                      <span className="board-clickup-filter-chip-label">Tipo de contrato</span>
                    </span>
                  </button>
                  {clientesTipoOpen ? (
                    <div
                      className="perm-clientes-tipo-popup"
                      role="listbox"
                      aria-label="Tipo de contrato"
                      aria-multiselectable="true"
                    >
                      {clientesTipoOptions.length ? (
                        clientesTipoOptions.map((opt) => {
                          const checked = clientesTiposSel.includes(opt);
                          return (
                            <button
                              key={opt}
                              type="button"
                              role="option"
                              aria-selected={checked}
                              className={`perm-clientes-tipo-item${checked ? ' is-selected' : ''}`}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setClientesTiposSel((prev) =>
                                  prev.includes(opt) ? prev.filter((x) => x !== opt) : [...prev, opt]
                                );
                              }}
                            >
                              <span className="perm-clientes-tipo-item-label" title={opt}>
                                {opt}
                              </span>
                            </button>
                          );
                        })
                      ) : (
                        <p className="perm-clientes-tipo-empty">Nenhum tipo disponível</p>
                      )}
                    </div>
                  ) : null}
                </div>
                <div className="perm-clientes-filtro-tipo">
                  <button
                    ref={clientesGrupoFilterWrapRef}
                    type="button"
                    className={`board-clickup-filter-chip perm-toolbar-chip${
                      hasActiveClientesGrupoFilter ? ' has-selection' : ''
                    }`}
                    onClick={() => {
                      setClientesTipoOpen(false);
                      setClientesGrupoFilterOpen((v) => !v);
                    }}
                    aria-expanded={clientesGrupoFilterOpen}
                    aria-label="Filtrar Grupo"
                  >
                    <span className="board-clickup-filter-chip-icon" aria-hidden>
                      <Funnel size={13} strokeWidth={1.5} />
                    </span>
                    <span className="board-clickup-filter-chip-text">
                      <span className="board-clickup-filter-chip-label">Grupo</span>
                    </span>
                  </button>
                </div>
                {showAjustarGrupoBtn ? (
                  <button
                    type="button"
                    className="board-clickup-filter-chip perm-toolbar-chip"
                    onClick={openGrupoBulkModal}
                    disabled={clientesLoading || !!clienteBusyKey || grupoBulkBusy}
                  >
                    <span className="board-clickup-filter-chip-text">
                      <span className="board-clickup-filter-chip-label">Ajustar grupo</span>
                    </span>
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>

        <div className="users-customers-surface perm-customers-surface">

      {oryaIntroOpen ? (
        <div className="perm-verve-modal-overlay" role="presentation" onClick={() => setOryaIntroOpen(false)}>
          <div
            className="perm-verve-modal perm-orya-intro-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="perm-orya-intro-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="perm-orya-intro-title" className="perm-verve-modal-title perm-orya-intro-title">
              <IconSparkles size={20} stroke={1.75} aria-hidden className="perm-verve-brain-ico" />
              <span>Organize com Orya</span>
            </h2>
            <ul className="perm-orya-intro-list">
              <li>Organizar a listagem filtrada e devolver o cadastro com sugestão de melhorias em Grupo e Subgrupo.</li>
              <li>Trazer cadastro identificado de equipamentos preto e branco e color para importação em massa.</li>
              <li>Requer filtro de Marca, Grupo ou Subgrupo ativo (sem listar todos os produtos).</li>
            </ul>
            <div className="perm-verve-modal-actions">
              <button type="button" className="perm-verve-btn-secondary" onClick={() => setOryaIntroOpen(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="perm-verve-btn-download"
                onClick={() => {
                  setOryaIntroOpen(false);
                  runVerveOrganize();
                }}
                disabled={verveBusy || !canOrganizeWithOrya}
              >
                Processar informações
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {permTab === 'clientes' && (
        <div className="perm-sup-main perm-sup-main--single">
          <section className="perm-panel perm-panel--equip" aria-label="Permissões de clientes">
            <div className="perm-sup-table-wrap perm-sup-table-wrap--clientes">
              <div className="perm-table-scroll perm-table-scroll--main perm-table-scroll--clientes">
                <table
                  className={`perm-table perm-table--sup perm-table--clientes${
                    techCfgExport ? ' perm-table--techcfg-export' : ''
                  }`}
                >
                  <thead>
                    <tr>
                      {techCfgExport ? (
                        <th className="perm-col-cli-check">
                          <label className="gr-check-label" aria-label="Selecionar todos">
                            <input
                              type="checkbox"
                              className="gr-check-native"
                              checked={techCfgExportAllState === 'all'}
                              ref={(el) => {
                                if (el) el.indeterminate = techCfgExportAllState === 'some';
                              }}
                              onChange={toggleTechCfgExportAll}
                              disabled={techCfgExportBusy || !techCfgExportTargets.length}
                            />
                            <span className="gr-check-box" aria-hidden />
                          </label>
                        </th>
                      ) : null}
                      <th className="perm-col-cli-contrato">Contrato</th>
                      <th className="perm-col-cli-cliente perm-th-sort" onClick={() => toggleClienteSort('cliente')}>
                        Cliente{clienteSortConfig.key === 'cliente' ? (clienteSortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                      <th className="perm-col-cli-bloq-fin">
                        <span className="perm-th-info-wrap perm-cli-th-info-wrap">
                          <span className="perm-cli-th-label">
                            Bloqueio
                            <br />
                            financeiro
                          </span>
                          <button type="button" className="perm-th-info-btn perm-th-info-btn--q" aria-label="Ajuda sobre Bloqueio financeiro">
                            ?
                          </button>
                          <div className="perm-th-tooltip-popover" role="tooltip">
                            Configure Bloqueio OS e Bloqueio Pedido e os dias de inadimplência para cada um.
                          </div>
                        </span>
                      </th>
                      <th className="perm-col-cli-ped-sup">
                        <span className="perm-cli-th-with-icon">
                          <IconAdjustmentsAlt size={14} stroke={1.75} aria-hidden />
                          <span className="perm-cli-th-label">
                            Pedido de
                            <br />
                            suprimento
                          </span>
                        </span>
                      </th>
                      <th className="perm-col-cli-chamado">
                        <span className="perm-cli-th-with-icon">
                          <IconAdjustmentsAlt size={14} stroke={1.75} aria-hidden />
                          <span className="perm-cli-th-label">Exibições</span>
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientesLoading ? (
                      <tr>
                        <td colSpan={techCfgExport ? 6 : 5} className="perm-empty">
                          <ProcessConfigSkeleton variant="tableBody" ariaLabel="Carregando permissões de clientes" />
                        </td>
                      </tr>
                    ) : clientesError ? (
                      <tr>
                        <td colSpan={techCfgExport ? 6 : 5} className="perm-empty">{clientesError}</td>
                      </tr>
                    ) : filteredClientesRows.length ? (
                      filteredClientesRows.map((row) => {
                        const rowKey = `${row.codigo}::${row.contrato}`;
                        const busy = clienteBusyKey === rowKey;
                        const bloqConfigured = row.opt_cob_os === 1 || row.opt_cob_sup === 1;
                        const bloqTip = bloqConfigured
                          ? 'Bloqueio financeiro configurado — clique para editar'
                          : 'Configurar bloqueios';
                        const pedSupConfigured =
                          row.opt_contador === 1 ||
                          row.opt_order_qty === 1 ||
                          row.opt_order_rma === 1 ||
                          row.opt_order_group === 1;
                        const pedSupTip = pedSupConfigured
                          ? 'Pedido de suprimento configurado — clique para editar'
                          : 'Configure permissões de pedido de suprimentos';
                        const chamadoConfigured = isTechCallConfigured(row.tech_call_cfg, row.overview_visibility);
                        const chamadoTip = chamadoConfigured
                          ? 'Exibições configuradas — clique para editar'
                          : 'Configurar exibições';
                        return (
                          <tr
                            key={rowKey}
                            className={`perm-tr perm-tr--model${
                              techCfgExport && techCfgExport.sourceKey === rowKey ? ' is-techcfg-source' : ''
                            }${techCfgExport && techCfgExportSelSet.has(rowKey) ? ' is-techcfg-selected' : ''}`}
                          >
                            {techCfgExport ? (
                              <td className="perm-td-cli-check">
                                {techCfgExport.sourceKey === rowKey ? (
                                  <span className="perm-techcfg-source-tag" title="Contrato de origem">
                                    Origem
                                  </span>
                                ) : (
                                  <label className="gr-check-label" aria-label={`Selecionar ${row.contrato}`}>
                                    <input
                                      type="checkbox"
                                      className="gr-check-native"
                                      checked={techCfgExportSelSet.has(rowKey)}
                                      onChange={() => toggleTechCfgExportRow(rowKey)}
                                      disabled={techCfgExportBusy}
                                    />
                                    <span className="gr-check-box" aria-hidden />
                                  </label>
                                )}
                              </td>
                            ) : null}
                            <td className="perm-td-cod perm-td-cli-contrato">
                              <PermEllipsisText value={row.contrato} />
                            </td>
                            <td className="perm-td-nome perm-td-cli-cliente">
                              <PermEllipsisText value={row.cliente} />
                            </td>
                            <td className="perm-td-cli-bloq-fin">
                              <div className="ci2-cell-wrap ci2-defeito-cell-inner">
                                <PermCfgTipWrap
                                  enabled={bloqConfigured}
                                  content={<BloqueioCfgTip row={row} />}
                                >
                                <button
                                  type="button"
                                  className={`ci2-cell-trigger ci2-defeito-trigger${
                                    bloqConfigured ? ' ci2-defeito-trigger--configured' : ' ci2-modern-tooltip-host'
                                  }${bloqFinPop?.rowKey === rowKey ? ' is-open' : ''}`}
                                  data-tooltip={bloqConfigured ? undefined : bloqTip}
                                  aria-label={bloqTip}
                                  disabled={busy}
                                  onClick={(e) => openBloqFinPop(e, row)}
                                >
                                  <span
                                    className={`ci2-defeito-gear-wrap${
                                      bloqConfigured ? ' ci2-defeito-gear-wrap--configured' : ''
                                    }`}
                                  >
                                    {bloqConfigured ? (
                                      <>
                                        <span className="ci2-defeito-status-check" aria-hidden>
                                          <Check size={10} strokeWidth={2.5} />
                                        </span>
                                        <Settings size={16} strokeWidth={1.75} className="ci2-defeito-icon" aria-hidden />
                                      </>
                                    ) : (
                                      <>
                                        <span className="ci2-defeito-idle-dash ci2-muted" aria-hidden>
                                          —
                                        </span>
                                        <Settings
                                          size={16}
                                          strokeWidth={1.75}
                                          className="ci2-defeito-icon ci2-defeito-icon--on-hover"
                                          aria-hidden
                                        />
                                      </>
                                    )}
                                  </span>
                                </button>
                                </PermCfgTipWrap>
                              </div>
                            </td>
                            <td className="perm-td-cli-ped-sup">
                              <div className="ci2-cell-wrap ci2-defeito-cell-inner">
                                <PermCfgTipWrap
                                  enabled={pedSupConfigured}
                                  content={<PedidoSupCfgTip row={row} options={PED_SUP_OPTIONS} />}
                                >
                                <button
                                  type="button"
                                  className={`ci2-cell-trigger ci2-defeito-trigger${
                                    pedSupConfigured
                                      ? ' ci2-defeito-trigger--configured perm-ped-sup-trigger--configured'
                                      : ' ci2-modern-tooltip-host'
                                  }${pedSupPop?.rowKey === rowKey ? ' is-open' : ''}`}
                                  data-tooltip={pedSupConfigured ? undefined : pedSupTip}
                                  aria-label={pedSupTip}
                                  disabled={busy}
                                  onClick={(e) => openPedSupPop(e, row)}
                                >
                                  <span
                                    className={`ci2-defeito-gear-wrap${
                                      pedSupConfigured ? ' perm-ped-sup-gear--configured' : ''
                                    }`}
                                  >
                                    {pedSupConfigured ? (
                                      <Settings size={16} strokeWidth={1.75} className="ci2-defeito-icon" aria-hidden />
                                    ) : (
                                      <>
                                        <span className="ci2-defeito-idle-dash ci2-muted" aria-hidden>
                                          —
                                        </span>
                                        <Settings
                                          size={16}
                                          strokeWidth={1.75}
                                          className="ci2-defeito-icon ci2-defeito-icon--on-hover"
                                          aria-hidden
                                        />
                                      </>
                                    )}
                                  </span>
                                </button>
                                </PermCfgTipWrap>
                              </div>
                            </td>
                            <td className="perm-td-cli-chamado">
                              <div className="ci2-cell-wrap ci2-defeito-cell-inner">
                                <PermCfgTipWrap
                                  enabled={chamadoConfigured}
                                  content={
                                    <ChamadoTecCfgTip
                                      techCallCfg={row.tech_call_cfg}
                                      overviewVisibility={row.overview_visibility}
                                    />
                                  }
                                >
                                <button
                                  type="button"
                                  className={`ci2-cell-trigger ci2-defeito-trigger${
                                    chamadoConfigured
                                      ? ' ci2-defeito-trigger--configured perm-ped-sup-trigger--configured'
                                      : ' ci2-modern-tooltip-host'
                                  }${chamadoTecModal?.rowKey === rowKey ? ' is-open' : ''}`}
                                  data-tooltip={chamadoConfigured ? undefined : chamadoTip}
                                  aria-label={chamadoTip}
                                  disabled={busy}
                                  onClick={() =>
                                    setChamadoTecModal({
                                      rowKey,
                                      contrato: row.contrato,
                                      cliente: row.cliente,
                                      codigo: row.codigo,
                                      tech_call_cfg: row.tech_call_cfg ?? null,
                                      overview_visibility: row.overview_visibility ?? null,
                                      row,
                                    })
                                  }
                                >
                                  <span
                                    className={`ci2-defeito-gear-wrap${
                                      chamadoConfigured ? ' perm-ped-sup-gear--configured' : ''
                                    }`}
                                  >
                                    {chamadoConfigured ? (
                                      <Settings size={16} strokeWidth={1.75} className="ci2-defeito-icon" aria-hidden />
                                    ) : (
                                      <>
                                        <span className="ci2-defeito-idle-dash ci2-muted" aria-hidden>
                                          —
                                        </span>
                                        <Settings
                                          size={16}
                                          strokeWidth={1.75}
                                          className="ci2-defeito-icon ci2-defeito-icon--on-hover"
                                          aria-hidden
                                        />
                                      </>
                                    )}
                                  </span>
                                </button>
                                </PermCfgTipWrap>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={techCfgExport ? 6 : 5} className="perm-empty">Nenhum item encontrado.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
          <PermBloqueioFinanceiroPopover
            open={!!bloqFinPop}
            anchorRef={bloqFinAnchorRef}
            draft={bloqFinPop?.draft || null}
            busy={bloqFinPop ? clienteBusyKey === bloqFinPop.rowKey : false}
            onChange={(next) =>
              setBloqFinPop((prev) => (prev ? { ...prev, draft: next } : prev))
            }
            onCancel={closeBloqFinPop}
            onConfirm={confirmBloqFinPop}
          />
          <PermPedidoSuprimentoPopover
            open={!!pedSupPop}
            anchorRef={pedSupAnchorRef}
            draft={pedSupPop?.draft || null}
            busy={pedSupPop ? clienteBusyKey === pedSupPop.rowKey : false}
            onChange={(next) =>
              setPedSupPop((prev) => (prev ? { ...prev, draft: next } : prev))
            }
            onCancel={closePedSupPop}
            onConfirm={confirmPedSupPop}
          />
          <PermChamadoTecnicoModal
            open={!!chamadoTecModal}
            onClose={() => setChamadoTecModal(null)}
            contrato={chamadoTecModal?.contrato}
            cliente={chamadoTecModal?.cliente}
            codigo={chamadoTecModal?.codigo}
            techCallCfg={chamadoTecModal?.tech_call_cfg}
            saving={chamadoTecModal ? clienteBusyKey === chamadoTecModal.rowKey : false}
            onSaveTechCallCfg={async (nextCfg) => {
              if (!chamadoTecModal?.row) return;
              await saveClienteRow(chamadoTecModal.row, { tech_call_cfg: nextCfg });
            }}
            overviewVisibility={chamadoTecModal?.overview_visibility}
            onSaveOverviewVisibility={async (nextVis) => {
              if (!chamadoTecModal?.row) return;
              await saveClienteRow(chamadoTecModal.row, { overview_visibility: nextVis });
            }}
            dataStartDates={chamadoTecModal?.row}
            onSaveDataStartDate={async (key, value) => {
              if (!chamadoTecModal?.row) return;
              await saveClienteRow(chamadoTecModal.row, { [key]: value });
            }}
            onExportConfig={startTechCfgExport}
          />
        </div>
      )}

      {permTab === 'suprimentos' && (
        <div className="perm-sup-main perm-sup-main--single">
          <section className="perm-panel perm-panel--equip" aria-label="Permissões de suprimentos">
            {bulkFeedback ? (
              <div
                className={`perm-bulk-banner perm-bulk-banner--${bulkFeedback.phase}`}
                role="status"
              >
                {bulkFeedback.phase === 'loading' && (
                  <>
                    <Loader2 className="perm-bulk-spin" size={16} aria-hidden />
                    <span>
                      Atualizando {bulkFeedback.count}{' '}
                      {bulkFeedback.count === 1 ? 'item' : 'itens'}…
                    </span>
                  </>
                )}
                {bulkFeedback.phase === 'success' && (
                  <>
                    <CheckCircle2 size={16} aria-hidden />
                    <span>
                      {bulkFeedback.count}{' '}
                      {bulkFeedback.count === 1 ? 'produto atualizado' : 'produtos atualizados'} —{' '}
                      {bulkFeedback.typesup === 'bk' ? 'Preto e branco' : 'Color'}
                    </span>
                    <button
                      type="button"
                      className="perm-bulk-banner-dismiss"
                      onClick={() => setBulkFeedback(null)}
                      aria-label="Fechar"
                    >
                      <X size={14} />
                    </button>
                  </>
                )}
                {bulkFeedback.phase === 'error' && (
                  <>
                    <AlertCircle size={16} aria-hidden />
                    <span>{bulkFeedback.message}</span>
                    <button
                      type="button"
                      className="perm-bulk-banner-dismiss"
                      onClick={() => setBulkFeedback(null)}
                      aria-label="Fechar"
                    >
                      <X size={14} />
                    </button>
                  </>
                )}
              </div>
            ) : null}
            <div className="perm-sup-table-wrap">
              {draggingBulk ? (
                <div className="perm-bulk-drops" aria-live="polite">
                  <div
                    className={`perm-bulk-drop ${bulkDropHover === 'bk' ? 'perm-bulk-drop--active' : ''}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'copy';
                      setBulkDropHover('bk');
                    }}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) setBulkDropHover(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleBulkDrop('bk');
                    }}
                  >
                    <span className="perm-bulk-drop-label">Preto e branco</span>
                    <span className="perm-bulk-drop-hint">Soltar seleção</span>
                  </div>
                  <div
                    className={`perm-bulk-drop perm-bulk-drop--color ${bulkDropHover === 'color' ? 'perm-bulk-drop--active' : ''}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'copy';
                      setBulkDropHover('color');
                    }}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) setBulkDropHover(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleBulkDrop('color');
                    }}
                  >
                    <span className="perm-bulk-drop-label">Color</span>
                    <span className="perm-bulk-drop-hint">Soltar seleção</span>
                  </div>
                </div>
              ) : null}
              <div
                ref={tableScrollRef}
                className="perm-table-scroll perm-table-scroll--main"
                onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
              >
              <table className="perm-table perm-table--sup">
                <thead>
                  <tr>
                    <th className="perm-col-cod perm-th-sort" onClick={() => toggleSort('codigo')}>Código{sortConfig.key === 'codigo' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</th>
                    <th className="perm-col-prod perm-th-sort" onClick={() => toggleSort('nomeProduto')}>Produto{sortConfig.key === 'nomeProduto' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</th>
                    <th className="perm-col-qtde">
                      <span ref={qtdeTipRef} className={`perm-th-info-wrap ${qtdeTipOpen ? 'perm-th-info-wrap--open' : ''}`}>
                        <button type="button" className="perm-th-sort-btn" onClick={() => toggleSort('qtde')}>
                          Qtde{sortConfig.key === 'qtde' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                        </button>
                        <button
                          type="button"
                          className="perm-th-info-btn"
                          aria-expanded={qtdeTipOpen}
                          aria-label="Ajuda sobre quantidade"
                          onClick={(e) => {
                            e.stopPropagation();
                            setQtdeTipOpen((v) => !v);
                          }}
                        >
                          <Info size={9} strokeWidth={2.25} />
                        </button>
                        {qtdeTipOpen ? (
                          <div className="perm-th-tooltip-popover" role="tooltip">
                            Quantidade locada.
                          </div>
                        ) : null}
                      </span>
                    </th>
                    <th className="perm-col-brand">
                      <div className="th-with-filter perm-th-with-filter">
                        <button type="button" className="perm-th-sort-btn" onClick={() => toggleSort('marca')}>
                          Marca{sortConfig.key === 'marca' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                        </button>
                        <button
                          ref={marcaFilterAnchorRef}
                          type="button"
                          className="filter-icon-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFilterCol((v) => (v === 'marca' ? '' : 'marca'));
                          }}
                          aria-label="Filtrar Marca"
                        >
                          <Funnel size={14} />
                          {hasActiveFilter('marca') ? <span className="perm-filter-badge-dot" aria-hidden /> : null}
                        </button>
                      </div>
                    </th>
                    <th className="perm-col-group">
                      <div className="th-with-filter perm-th-with-filter">
                        <button type="button" className="perm-th-sort-btn" onClick={() => toggleSort('grupo')}>
                          Grupo{sortConfig.key === 'grupo' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                        </button>
                        <button
                          ref={grupoFilterAnchorRef}
                          type="button"
                          className="filter-icon-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFilterCol((v) => (v === 'grupo' ? '' : 'grupo'));
                          }}
                          aria-label="Filtrar Grupo"
                        >
                          <Funnel size={14} />
                          {hasActiveFilter('grupo') ? <span className="perm-filter-badge-dot" aria-hidden /> : null}
                        </button>
                      </div>
                    </th>
                    <th className="perm-col-subgroup">
                      <div className="th-with-filter perm-th-with-filter">
                        <button type="button" className="perm-th-sort-btn" onClick={() => toggleSort('subgrupo')}>
                          Subgrupo{sortConfig.key === 'subgrupo' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                        </button>
                        <button
                          ref={subgrupoFilterAnchorRef}
                          type="button"
                          className="filter-icon-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenFilterCol((v) => (v === 'subgrupo' ? '' : 'subgrupo'));
                          }}
                          aria-label="Filtrar Subgrupo"
                        >
                          <Funnel size={14} />
                          {hasActiveFilter('subgrupo') ? <span className="perm-filter-badge-dot" aria-hidden /> : null}
                        </button>
                      </div>
                    </th>
                    <th className="perm-col-toggle perm-th-sort" onClick={() => toggleSort('typesup')}>Preto e branco{sortConfig.key === 'typesup' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</th>
                    <th className="perm-col-toggle perm-th-sort" onClick={() => toggleSort('typesup')}>Color{sortConfig.key === 'typesup' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}</th>
                    <th className="perm-col-actions" aria-label="Foto" />
                  </tr>
                </thead>
                <tbody ref={tableBodyRef}>
                  {listLoading ? (
                    <tr>
                      <td colSpan={9} className="perm-empty">
                        <ProcessConfigSkeleton variant="tableBody" ariaLabel="Carregando permissões de equipamentos" />
                      </td>
                    </tr>
                  ) : listError ? (
                    <tr>
                      <td colSpan={9} className="perm-empty">
                        {listError}
                      </td>
                    </tr>
                  ) : tableRows.length ? (
                    <>
                      {topSpacerHeight > 0 ? (
                        <tr className="perm-tr-spacer" aria-hidden>
                          <td colSpan={9} style={{ height: `${topSpacerHeight}px` }} />
                        </tr>
                      ) : null}
                      {virtualRows.map((row) => {
                      const codigo = String(row.codigo || '').trim();
                      const isBk = String(row.typesup || '').toLowerCase() === 'bk';
                      const isColor = String(row.typesup || '').toLowerCase() === 'color';
                      const rowToggleBusy =
                        toggleBusy !== '' && toggleBusy.split(':')[0] === codigo;
                      const rowSel = selectedSet.has(codigo);
                      const togglesLocked = rowToggleBusy || hasSelection || bulkFeedback?.phase === 'loading';
                      return (
                        <tr
                          key={codigo}
                          role="row"
                          aria-selected={rowSel}
                          draggable={hasSelection && rowSel && bulkFeedback?.phase !== 'loading'}
                          className={`perm-tr perm-tr--model${rowSel ? ' perm-tr--sel' : ''}${draggingBulk && rowSel ? ' perm-tr--dragging' : ''}`}
                          style={{
                            cursor: hasSelection && rowSel ? 'grab' : 'pointer',
                          }}
                          onClick={(e) => {
                            if (e.target.closest('.ci2-switch')) return;
                            if (e.target.closest('.perm-td-actions')) return;
                            toggleRowSelected(codigo);
                          }}
                          onDragStart={onBulkDragStart}
                          onDragEnd={onBulkDragEnd}
                        >
                          <td className="perm-td-cod">
                            <PermEllipsisText value={codigo} />
                          </td>
                          <td className="perm-td-nome">
                            <PermEllipsisText value={row.nomeProduto} />
                          </td>
                          <td className="perm-td-n">{(Number(row.qtde) || 0).toLocaleString('pt-BR')}</td>
                          <td className="perm-td-nome">
                            <PermEllipsisText value={row.marca} />
                          </td>
                          <td className="perm-td-nome">
                            <PermEllipsisText value={row.grupo} />
                          </td>
                          <td className="perm-td-nome">
                            <PermEllipsisText value={row.subgrupo} />
                          </td>
                          <td className="perm-td-toggle">
                            <PermSwitch
                              checked={isBk}
                              disabled={togglesLocked}
                              ariaLabel="Preto e branco"
                              onChange={(v) => {
                                if (isBk && !v) toggleTypeSup(codigo, '');
                                else if (!isBk && v) toggleTypeSup(codigo, 'bk');
                              }}
                            />
                          </td>
                          <td className="perm-td-toggle">
                            <PermSwitch
                              checked={isColor}
                              disabled={togglesLocked}
                              ariaLabel="Color"
                              onChange={(v) => {
                                if (isColor && !v) toggleTypeSup(codigo, '');
                                else if (!isColor && v) toggleTypeSup(codigo, 'color');
                              }}
                            />
                          </td>
                          <PermEquipRowMenu
                            codigo={codigo}
                            hasImage={Boolean(equipImageMap[codigo])}
                            onDefinePhoto={() => openEquipPhotoUpload(row)}
                            onViewPhoto={() => openEquipPhotoView(row)}
                          />
                        </tr>
                      );
                      })}
                      {bottomSpacerHeight > 0 ? (
                        <tr className="perm-tr-spacer" aria-hidden>
                          <td colSpan={9} style={{ height: `${bottomSpacerHeight}px` }} />
                        </tr>
                      ) : null}
                    </>
                  ) : (
                    <tr>
                      <td colSpan={9} className="perm-empty">
                        Nenhum item encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </section>
        </div>
      )}

        </div>
      </section>

      {verveIaPhase !== 'idle' ? (
        <div className="perm-verve-modal-overlay" role="presentation">
          <div
            className="perm-verve-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="perm-verve-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            {verveIaPhase === 'loading' ? (
              <>
                <div className="perm-verve-modal-visual" aria-hidden>
                  <IconSparkles className="perm-verve-brain-pulse" size={40} stroke={1.75} />
                  <Loader2 className="perm-verve-orbit" size={22} strokeWidth={2} />
                </div>
                <h2 id="perm-verve-modal-title" className="perm-verve-modal-title">
                  Orya
                </h2>
                <p className="perm-verve-loading-text">{VERVE_LOADING_MESSAGES[verveLoadingMsgIdx]}</p>
              </>
            ) : null}
            {verveIaPhase === 'error' ? (
              <>
                <h2 id="perm-verve-modal-title" className="perm-verve-modal-title">
                  Não foi possível concluir
                </h2>
                <p className="perm-verve-error-text">{verveIaError}</p>
                <div className="perm-verve-modal-actions">
                  <button type="button" className="perm-verve-btn-secondary" onClick={closeVerveModal}>
                    Fechar
                  </button>
                </div>
              </>
            ) : null}
            {verveIaPhase === 'result' && verveIaResult ? (
              <>
                <h2 id="perm-verve-modal-title" className="perm-verve-modal-title">
                  Análise e sugestão de cadastro
                </h2>
                <div className="perm-verve-analysis-scroll">
                  <p className="perm-verve-analysis-text">{verveIaResult.analise}</p>
                  <p className="perm-verve-result-meta">
                    {verveIaResult.rows.length}{' '}
                    {verveIaResult.rows.length === 1 ? 'produto' : 'produtos'} na sugestão (reflete a tabela
                    filtrada).
                  </p>
                </div>
                <div className="perm-verve-modal-actions">
                  <button
                    type="button"
                    className="perm-verve-btn-download"
                    onClick={onDownloadVerveXlsx}
                  >
                    <HardDriveDownload size={18} strokeWidth={2} aria-hidden />
                    Baixe a estrutura sugerida
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {importModalOpen ? (
        <div className="perm-verve-modal-overlay perm-import-modal-overlay" role="presentation">
          <div
            className="perm-verve-modal perm-import-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="perm-import-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="perm-import-modal-close"
              onClick={closeImportModal}
              disabled={importProcessing}
              aria-label="Fechar"
            >
              <X size={18} />
            </button>
            <div className="perm-import-modal-head">
              <HardDriveUpload size={28} strokeWidth={1.75} className="perm-import-modal-ico" aria-hidden />
              <h2 id="perm-import-modal-title" className="perm-import-modal-title">
                Importar atualização em massa
              </h2>
            </div>
            <p className="perm-import-intro">
              Atualize o tipo de suprimento (preto e branco ou colorido) para os produtos{' '}
              <strong>visíveis nesta listagem</strong> (respeitando busca e filtros atuais). Arquivo{' '}
              <strong>.xlsx</strong>, uma linha por produto.
            </p>
            <ul className="perm-import-rules">
              <li>
                <strong>Coluna A — Código:</strong> exatamente <strong>5 dígitos</strong> (ex.:{' '}
                <code>02410</code>).
              </li>
              <li>
                <strong>Coluna B — Preto e branco:</strong> <code>1</code> ou <code>0</code> (um dígito).
              </li>
              <li>
                <strong>Coluna C — Colorido:</strong> <code>1</code> ou <code>0</code> (um dígito).
              </li>
              <li>
                Em cada linha, uma coluna deve ser <code>1</code> e a outra <code>0</code> (nunca ambas
                iguais).
              </li>
            </ul>
            <input
              ref={importFileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="perm-import-file-input"
              aria-hidden
              tabIndex={-1}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                if (f) loadImportFile(f);
              }}
            />
            <div
              className={`perm-import-drop ${importDragOver ? 'perm-import-drop--active' : ''} ${importFile ? 'perm-import-drop--has-file' : ''}`}
              onDragEnter={(e) => {
                e.preventDefault();
                setImportDragOver(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setImportDragOver(true);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setImportDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setImportDragOver(false);
                const f = e.dataTransfer.files && e.dataTransfer.files[0];
                if (f) loadImportFile(f);
              }}
            >
              {importFile ? (
                <div className="perm-import-file-selected">
                  <HardDriveUpload size={22} strokeWidth={1.75} className="perm-import-file-ico" aria-hidden />
                  <span className="perm-import-file-name" title={importFile.name}>
                    {importFile.name}
                  </span>
                  <button
                    type="button"
                    className="perm-import-file-remove"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      clearImportFile();
                    }}
                    disabled={importProcessing}
                    aria-label="Remover arquivo"
                  >
                    <X size={16} strokeWidth={2.25} />
                  </button>
                  <p className="perm-import-drop-hint perm-import-drop-hint--file">
                    Para trocar, remova o arquivo ou arraste outro .xlsx aqui.
                  </p>
                </div>
              ) : (
                <>
                  <p className="perm-import-drop-title">Arraste um arquivo .xlsx aqui</p>
                  <p className="perm-import-drop-sub">ou</p>
                  <button
                    type="button"
                    className="perm-import-pick-btn"
                    disabled={importProcessing}
                    onClick={() => importFileInputRef.current && importFileInputRef.current.click()}
                  >
                    Selecionar arquivo
                  </button>
                  <p className="perm-import-drop-hint">Apenas um arquivo por vez.</p>
                </>
              )}
            </div>
            {importValidation && importValidation.errors.length > 0 ? (
              <div className="perm-import-errors" role="alert">
                <div className="perm-import-errors-title">
                  <AlertCircle size={16} aria-hidden /> Ajuste a planilha
                </div>
                <ul className="perm-import-errors-list">
                  {importValidation.errors.map((err, idx) => (
                    <li key={`${idx}-${err.slice(0, 24)}`}>{err}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {importValidation?.ok ? (
              <div className="perm-import-success-note" role="status">
                <CheckCircle2 size={16} aria-hidden /> {importValidation.items.length} linha(s) válida(s)
                para processar.
              </div>
            ) : null}
            {importResultMsg ? (
              <div className="perm-import-result-ok" role="status">
                {importResultMsg}
              </div>
            ) : null}
            <div className="perm-import-actions">
              <button
                type="button"
                className="perm-verve-btn-secondary"
                onClick={closeImportModal}
                disabled={importProcessing}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="perm-import-process-btn"
                onClick={runImportProcess}
                disabled={!importValidation?.ok || importProcessing || !!importResultMsg}
              >
                {importProcessing ? (
                  <>
                    <Loader2 className="perm-bulk-spin" size={16} aria-hidden />
                    Processando…
                  </>
                ) : (
                  'Processar'
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {grupoBulkOpen ? (
        <div
          className="perm-verve-modal-overlay"
          role="presentation"
          onClick={() => !grupoBulkBusy && setGrupoBulkOpen(false)}
        >
          <div
            className="perm-verve-modal perm-grupo-bulk-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="perm-grupo-bulk-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="perm-grupo-bulk-title" className="perm-verve-modal-title perm-grupo-bulk-title">
              Ajustar grupo
              {clientesGrupoSel[0] ? (
                <span className="perm-grupo-bulk-sub"> — {clientesGrupoSel[0]}</span>
              ) : null}
            </h2>
            <p className="perm-grupo-bulk-hint">
              A alteração será aplicada a {filteredClientesRows.length} contrato(s) visíveis nesta listagem.
            </p>
            <div className="perm-grupo-bulk-fields">
              <div className="perm-grupo-bulk-row">
                <span className="perm-grupo-bulk-label">Contador</span>
                <PermSwitch
                  checked={grupoBulkDraft.opt_contador}
                  disabled={grupoBulkBusy}
                  ariaLabel="Contador"
                  onChange={(v) => setGrupoBulkDraft((d) => ({ ...d, opt_contador: v }))}
                />
              </div>
              <div className="perm-grupo-bulk-row">
                <span className="perm-grupo-bulk-label">Bloqueio OS</span>
                <div className="perm-grupo-bulk-row-controls">
                  <PermSwitch
                    checked={grupoBulkDraft.opt_cob_os}
                    disabled={grupoBulkBusy}
                    ariaLabel="Bloqueio OS"
                    onChange={(v) =>
                      setGrupoBulkDraft((d) => ({
                        ...d,
                        opt_cob_os: v,
                        opt_cob_os_dias: v ? d.opt_cob_os_dias : '',
                      }))
                    }
                  />
                  {grupoBulkDraft.opt_cob_os ? (
                    <div className="perm-cli-days-wrap">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="perm-cli-days-input"
                        value={grupoBulkDraft.opt_cob_os_dias}
                        onChange={(e) =>
                          setGrupoBulkDraft((d) => ({
                            ...d,
                            opt_cob_os_dias: sanitizeDaysInput(e.target.value),
                          }))
                        }
                        aria-label="Dias bloqueio OS"
                        disabled={grupoBulkBusy}
                      />
                      <span className="perm-cli-days-label">dias</span>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="perm-grupo-bulk-row">
                <span className="perm-grupo-bulk-label">Bloqueio Pedido</span>
                <div className="perm-grupo-bulk-row-controls">
                  <PermSwitch
                    checked={grupoBulkDraft.opt_cob_sup}
                    disabled={grupoBulkBusy}
                    ariaLabel="Bloqueio Pedido"
                    onChange={(v) =>
                      setGrupoBulkDraft((d) => ({
                        ...d,
                        opt_cob_sup: v,
                        opt_cob_sup_dias: v ? d.opt_cob_sup_dias : '',
                      }))
                    }
                  />
                  {grupoBulkDraft.opt_cob_sup ? (
                    <div className="perm-cli-days-wrap">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="perm-cli-days-input"
                        value={grupoBulkDraft.opt_cob_sup_dias}
                        onChange={(e) =>
                          setGrupoBulkDraft((d) => ({
                            ...d,
                            opt_cob_sup_dias: sanitizeDaysInput(e.target.value),
                          }))
                        }
                        aria-label="Dias bloqueio pedido"
                        disabled={grupoBulkBusy}
                      />
                      <span className="perm-cli-days-label">dias</span>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="perm-grupo-bulk-row">
                <span className="perm-grupo-bulk-label">Pedido Quantidade</span>
                <PermSwitch
                  checked={grupoBulkDraft.opt_order_qty}
                  disabled={grupoBulkBusy}
                  ariaLabel="Pedido Quantidade"
                  onChange={(v) => setGrupoBulkDraft((d) => ({ ...d, opt_order_qty: v }))}
                />
              </div>
              <div className="perm-grupo-bulk-row">
                <span className="perm-grupo-bulk-label">Pedido RMA</span>
                <PermSwitch
                  checked={grupoBulkDraft.opt_order_rma}
                  disabled={grupoBulkBusy}
                  ariaLabel="Pedido RMA"
                  onChange={(v) => setGrupoBulkDraft((d) => ({ ...d, opt_order_rma: v }))}
                />
              </div>
              <div className="perm-grupo-bulk-row">
                <span className="perm-grupo-bulk-label">Agrupar Pedido</span>
                <PermSwitch
                  checked={grupoBulkDraft.opt_order_group}
                  disabled={grupoBulkBusy}
                  ariaLabel="Agrupar Pedido"
                  onChange={(v) => setGrupoBulkDraft((d) => ({ ...d, opt_order_group: v }))}
                />
              </div>
            </div>
            <div className="perm-verve-modal-actions">
              <button
                type="button"
                className="perm-verve-btn-secondary"
                onClick={() => !grupoBulkBusy && setGrupoBulkOpen(false)}
                disabled={grupoBulkBusy}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="perm-verve-btn-download"
                onClick={confirmGrupoBulk}
                disabled={grupoBulkBusy || !filteredClientesRows.length}
              >
                {grupoBulkBusy ? (
                  <>
                    <Loader2 className="perm-bulk-spin" size={16} aria-hidden />
                    Processando…
                  </>
                ) : (
                  'Confirmar ajuste'
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {verveInfoModal ? (
        <div
          className="perm-verve-modal-overlay perm-verve-info-overlay"
          role="presentation"
          onClick={closeVerveInfoModal}
        >
          <div
            className={`perm-verve-modal perm-verve-info-modal perm-verve-info-modal--${verveInfoModal.variant}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="perm-verve-info-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="perm-verve-info-icon-wrap" aria-hidden>
              {verveInfoModal.variant === 'limit' ? (
                <CalendarClock size={36} strokeWidth={1.6} className="perm-verve-info-icon" />
              ) : verveInfoModal.variant === 'error' ? (
                <AlertCircle size={36} strokeWidth={1.6} className="perm-verve-info-icon" />
              ) : (
                <Info size={36} strokeWidth={1.6} className="perm-verve-info-icon" />
              )}
            </div>
            <h2 id="perm-verve-info-modal-title" className="perm-verve-info-title">
              {verveInfoModal.title}
            </h2>
            <p className="perm-verve-info-msg">{verveInfoModal.message}</p>
            {Array.isArray(verveInfoModal.missing) && verveInfoModal.missing.length > 0 ? (
              <ul className="perm-verve-info-missing">
                {verveInfoModal.missing.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            ) : null}
            {Array.isArray(verveInfoModal.hints) && verveInfoModal.hints.length > 0 ? (
              <div className="perm-verve-info-hint" role="note">
                <strong>Onde configurar</strong>
                <ol className="perm-verve-info-hint-list">
                  {verveInfoModal.hints.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ol>
              </div>
            ) : null}
            <div className="perm-verve-modal-actions">
              <button
                type="button"
                className="user-form-profile-save-btn user-form-footer-btn"
                onClick={closeVerveInfoModal}
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <PermColumnFilterPopover
        anchorRef={marcaFilterAnchorRef}
        isOpen={openFilterCol === 'marca'}
        onClose={() => setOpenFilterCol('')}
        options={filterOptions.marca}
        selected={columnFilters.marca}
        onApply={(vals) => setColumnFilters((f) => ({ ...f, marca: vals }))}
        listAriaLabel="Filtrar Marca"
      />
      <PermColumnFilterPopover
        anchorRef={grupoFilterAnchorRef}
        isOpen={openFilterCol === 'grupo'}
        onClose={() => setOpenFilterCol('')}
        options={filterOptions.grupo}
        selected={columnFilters.grupo}
        onApply={(vals) => setColumnFilters((f) => ({ ...f, grupo: vals }))}
        listAriaLabel="Filtrar Grupo"
      />
      <PermColumnFilterPopover
        anchorRef={subgrupoFilterAnchorRef}
        isOpen={openFilterCol === 'subgrupo'}
        onClose={() => setOpenFilterCol('')}
        options={filterOptions.subgrupo}
        selected={columnFilters.subgrupo}
        onApply={(vals) => setColumnFilters((f) => ({ ...f, subgrupo: vals }))}
        listAriaLabel="Filtrar Subgrupo"
      />
      <PermColumnFilterPopover
        anchorRef={clientesGrupoFilterWrapRef}
        isOpen={clientesGrupoFilterOpen}
        onClose={() => setClientesGrupoFilterOpen(false)}
        options={clientesGrupoOptions}
        selected={clientesGrupoSel}
        onApply={(vals) => setClientesGrupoSel(vals)}
        listAriaLabel="Filtrar Grupo de clientes"
        searchPlaceholder="Buscar grupo"
        popoverClassName="board-search-filters-popover board-search-filters-popover--simple board-search-filters-popover--tecnico"
      />
      <PermEquipPhotoModal
        open={Boolean(equipPhotoModal)}
        mode={equipPhotoModal?.mode || 'upload'}
        codigo={equipPhotoModal?.codigo || ''}
        produtoNome={equipPhotoModal?.produtoNome || ''}
        viewUrl={equipPhotoModal?.viewUrl || ''}
        onClose={() => setEquipPhotoModal(null)}
        onUpload={(file) => uploadEquipPhoto(equipPhotoModal?.codigo, file)}
      />
    </div>
  );
}
